/*
  BagSafe luggage storage: pricing engine + booking flow.

  Loaded (deferred) only by sections/bagsafe-storage.liquid. Everything is scoped to
  each [data-bsl] root, so it never touches the shipping wizard on the same page
  (that one uses the fc- prefix, [data-panel] and its own ids).

  Money is handled in paise (integers) end to end. Prices come from the Shopify
  variants embedded by the section, so the quote always equals what checkout charges.
*/
(function (global) {
  'use strict';

  /* ======================= PRICING ENGINE (pure) ======================= */

  var SIZE_KEYS = ['p', 'c', 'k'];               // personal, cabin, check-in (option position)
  var PLAN_KEYS = ['h1', 'h2', 'half', 'day'];   // 1 hour, 2 hours, half day (3-9 h), full day (24 h)

  /* Price one bag for `hours` (rounded up to whole hours).
     The brief's rules: hourly for 1-2 h, the half-day cap for 3-9 h, the day cap beyond
     that, repeating per 24 h, and the auto-cap: whenever a cheaper flat cap covers the
     same time, use it. `v` holds paise prices: { h1, h2, half, day }.
     Returns { total, units: [{ plan, qty }] } so the cart can be built from real variants. */
  function planBag(hours, v) {
    var h = Math.max(1, Math.ceil(hours));
    var days = Math.floor(h / 24);
    var r = h % 24;
    var slabPlan = null;
    var slab = 0;
    if (r > 0) {
      var options = [];
      if (r === 1) options.push(['h1', v.h1]);
      if (r === 2) options.push(['h2', v.h2]);
      if (r <= 9) options.push(['half', v.half]);
      options.push(['day', v.day]);
      options.sort(function (a, b) { return a[1] - b[1]; });
      slabPlan = options[0][0];
      slab = options[0][1];
    }
    var units = [];
    var dayQty = days + (slabPlan === 'day' ? 1 : 0);
    if (dayQty) units.push({ plan: 'day', qty: dayQty });
    if (slabPlan && slabPlan !== 'day') units.push({ plan: slabPlan, qty: 1 });
    return { total: days * v.day + slab, units: units, hours: h };
  }

  /* Full quote. cfg: parsed section config; st: { counts, hours, protect, legs }.
     Pickup, return & handling is charged once per booking at the rate of its heaviest
     bag class. It is a flat handling charge (sealing, tagging, photos, storage; never
     waived) plus one part per leg an agent travels (st.legs.pickup / st.legs.drop, each
     'agent' or 'self', where 'self' = the customer comes to the hub):
       both legs by agent -> cfg.handling[key] (two-way)
       one leg by agent   -> cfg.handlingOne[key] (handling + one trip)
       no agent legs      -> cfg.handlingHub[key] (handling only)
     An unset product falls back to the next dearer one, so a booking is never
     undercharged. Reduced lines carry `was` (the two-way base) for the price card.
     Returns lines (for display and cart) plus subtotal / gst / total in paise. */
  function quote(cfg, st) {
    var lines = [];
    var cart = {};          // variantId -> qty
    var total = 0;
    var base = 0;
    var div = 1 + (cfg.gst || 0) / 100;

    function add(variant, qty) {
      var b = Math.round(variant.p / div) * qty;
      cart[variant.id] = (cart[variant.id] || 0) + qty;
      total += variant.p * qty;
      base += b;
      return b;
    }

    SIZE_KEYS.forEach(function (key) {
      var n = st.counts[key] || 0;
      var size = cfg.sizes[key];
      if (!n || !size) return;
      var prices = {};
      PLAN_KEYS.forEach(function (pk) { prices[pk] = size.v[pk] ? size.v[pk].p : Infinity; });
      var plan = planBag(st.hours, prices);
      var amount = 0;
      var lineBase = 0;
      plan.units.forEach(function (u) {
        lineBase += add(size.v[u.plan], u.qty * n);
        amount += size.v[u.plan].p * u.qty * n;
      });
      lines.push({ kind: 'storage', key: key, count: n, name: size.name, units: plan.units, amount: amount, base: lineBase });
    });

    // One agent trip per booking, so one handling charge: the rate of the heaviest
    // bag class in the booking (owner's call, 4 Oct 2026). `covers` lists every class
    // it pays for, for the quote text.
    var handling = cfg.handling || {};
    var oneWay = cfg.handlingOne || {};
    var atHub = cfg.handlingHub || {};
    var legs = st.legs || {};
    var agentLegs = (legs.pickup === 'self' ? 0 : 1) + (legs.drop === 'self' ? 0 : 1);
    var top = null, covers = [];
    SIZE_KEYS.forEach(function (key) {
      var n = st.counts[key] || 0;
      if (!n || !cfg.sizes[key]) return;
      covers.push({ key: key, count: n });
      if (handling[key]) top = key;
    });
    if (top) {
      var hv = handling[top];
      var full = Math.round(hv.p / div);
      if (agentLegs === 2) {
        lines.push({ kind: 'handling', legs: 2, key: top, count: 1, each: hv.p, amount: hv.p, base: add(hv, 1), covers: covers });
      } else if (agentLegs === 1) {
        var ov = oneWay[top] || hv;
        var ob = add(ov, 1);
        lines.push({ kind: 'handling', legs: 1, key: top, count: 1, each: ov.p, amount: ov.p, base: ob, was: full, covers: covers });
      } else {
        var hb = atHub[top] || oneWay[top] || hv;
        var hbb = add(hb, 1);
        lines.push({ kind: 'handling', legs: 0, key: top, count: 1, each: hb.p, amount: hb.p, base: hbb, was: full, covers: covers });
      }
    }

    var pr = cfg.protection;
    if (pr && pr.mode === 'included' && lines.length) {
      // BagSafe's own free cover: shown in the quote, never in the cart or the total.
      lines.push({ kind: 'protection', included: true, amount: 0, base: 0 });
    } else if (pr && pr.id && lines.length && (pr.mode === 'mandatory' || (pr.mode === 'optional' && st.protect))) {
      lines.push({ kind: 'protection', amount: pr.p, base: add(pr, 1) });
    }

    return { lines: lines, cart: cart, total: total, subtotal: base, gst: total - base };
  }

  var Engine = { planBag: planBag, quote: quote, SIZE_KEYS: SIZE_KEYS, PLAN_KEYS: PLAN_KEYS };
  global.BagSafeStorageEngine = Engine;
  if (typeof module !== 'undefined' && module.exports) { module.exports = Engine; return; }

  /* ======================= TIME HELPERS (Kolkata time) ======================= */

  var IST_MIN = 330;
  var DAY_MIN = 1440;
  var DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  var MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  function nowIST() {
    var d = new Date(Date.now() + IST_MIN * 60000);
    return { iso: d.toISOString().slice(0, 10), min: d.getUTCHours() * 60 + d.getUTCMinutes() };
  }
  function addDays(iso, n) {
    var d = new Date(iso + 'T00:00:00Z');
    d.setUTCDate(d.getUTCDate() + n);
    return d.toISOString().slice(0, 10);
  }
  function toMin(t) {
    var p = String(t || '0:0').split(':');
    return (parseInt(p[0], 10) || 0) * 60 + (parseInt(p[1], 10) || 0);
  }
  function fmtTime(min) {
    var h = Math.floor(min / 60) % 24, m = min % 60;
    return (h % 12 || 12) + ':' + (m < 10 ? '0' : '') + m + ' ' + (h < 12 ? 'AM' : 'PM');
  }
  function fmtDate(iso) {
    var d = new Date(iso + 'T00:00:00Z');
    return DOW[d.getUTCDay()] + ', ' + d.getUTCDate() + ' ' + MON[d.getUTCMonth()];
  }
  function fmtDateLong(iso) {
    var d = new Date(iso + 'T00:00:00Z');
    return fmtDate(iso) + ' ' + d.getUTCFullYear();
  }
  /* Absolute minutes since 1970-01-01 (Kolkata wall time), so stays can cross midnight. */
  function absOf(iso, min) { return Math.round(Date.parse(iso + 'T00:00:00Z') / 86400000) * 1440 + min; }
  function isoOfAbs(abs) { return new Date(Math.floor(abs / 1440) * 86400000).toISOString().slice(0, 10); }
  function pad(n) { return (n < 10 ? '0' : '') + n; }
  function dtLocal(abs) { var m = ((abs % 1440) + 1440) % 1440; return isoOfAbs(abs) + 'T' + pad(Math.floor(m / 60)) + ':' + pad(m % 60); }
  function fmtHours(h) {
    var d = Math.floor(h / 24), r = h % 24, out = [];
    if (d) out.push(d + (d === 1 ? ' day' : ' days'));
    if (r) out.push(r + (r === 1 ? ' hour' : ' hours'));
    return out.join(' ');
  }
  function money(paise) {
    var r = paise / 100;
    var whole = Math.round(r) === r;
    return '₹' + r.toLocaleString('en-IN', { minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: 2 });
  }

  /* ======================= CALENDAR + CLOCK POPOVERS =======================
     Ported from the shipping wizard's date grid and analog clock
     (sections/arivah_checkout_step_1.liquid: makeDatePicker / makeClockPicker), so
     both services pick dates and times the same way, re-scoped to bsl- classes and
     run in Kolkata time. Pick a day = select and close; tap an hour, then a minute
     (5-minute ticks) = select and close. Disabled days and times can't be chosen by
     touch, mouse or keyboard. */
  var CAL_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  var CAL_DOW = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
  var CAL_DOW_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  var CHEV_L = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15 18l-6-6 6-6"/></svg>';
  var CHEV_R = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 18l6-6-6-6"/></svg>';
  var DIAL_R = 92, DIAL_C = 120;   // must match the 240px dial in the CSS

  function escHtml(s) { return String(s).replace(/[&<>"']/g, function (ch) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]; }); }
  function isoParts(iso) { var p = String(iso).split('-'); return { y: +p[0], m: +p[1] - 1, d: +p[2] }; }
  function isoOf(y, m, d) { return y + '-' + pad(m + 1) + '-' + pad(d); }
  function dowOf(iso) { return new Date(iso + 'T00:00:00Z').getUTCDay(); }
  function fmtDayFull(iso) { var p = isoParts(iso); return CAL_DOW_FULL[dowOf(iso)] + ', ' + p.d + ' ' + CAL_MONTHS[p.m] + ' ' + p.y; }
  function dialPos(i) { var a = (i * 30 - 90) * Math.PI / 180; return { x: DIAL_C + DIAL_R * Math.cos(a), y: DIAL_C + DIAL_R * Math.sin(a) }; }

  /* Shared popover shell: open/close, outside tap, Escape, Tab kept inside.
     Outside taps use pointerdown (+ mousedown/touchstart), not focusout: iOS Safari
     doesn't reliably blur a focused button when you tap elsewhere. */
  function makePopover(trigger, panel, onOpen) {
    var wrap = trigger.parentNode;
    var open = false, outside = null;
    var EVTS = ['pointerdown', 'mousedown', 'touchstart'];
    function reposition() {
      panel.removeAttribute('data-up');
      var r = trigger.getBoundingClientRect();
      if (window.innerHeight - r.bottom < 400 && r.top > 400) panel.setAttribute('data-up', '');
    }
    function focusFirst() { var f = panel.querySelector('[tabindex="0"]') || panel.querySelector('button:not([disabled])'); if (f) f.focus(); }
    function show() {
      if (open) return;
      open = true;
      onOpen();
      panel.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
      wrap.setAttribute('data-open', '');
      reposition();
      focusFirst();
      outside = function (e) { if (!wrap.contains(e.target)) hide(true); };
      EVTS.forEach(function (t) { document.addEventListener(t, outside, true); });
    }
    function hide(skipFocus) {
      if (!open) return;
      open = false;
      panel.hidden = true;
      trigger.setAttribute('aria-expanded', 'false');
      wrap.removeAttribute('data-open');
      EVTS.forEach(function (t) { document.removeEventListener(t, outside, true); });
      outside = null;
      if (!skipFocus) trigger.focus();
    }
    trigger.addEventListener('click', function () { if (open) hide(); else show(); });
    trigger.addEventListener('keydown', function (e) {
      if (open) return;
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') { e.preventDefault(); show(); }
    });
    panel.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' || e.key === 'Esc') { e.preventDefault(); hide(); return; }
      if (e.key === 'Tab') {
        var f = Array.prototype.slice.call(panel.querySelectorAll('button:not([disabled])'));
        if (!f.length) return;
        var i = f.indexOf(document.activeElement);
        if (e.shiftKey && i <= 0) { e.preventDefault(); f[f.length - 1].focus(); }
        else if (!e.shiftKey && i === f.length - 1) { e.preventDefault(); f[0].focus(); }
      }
    });
    return { show: show, hide: hide, isOpen: function () { return open; }, focusFirst: focusFirst };
  }

  /* o: { trigger, panel, min(), max(), value(), onPick(iso) } -- ISO dates, Kolkata. */
  function makeCalendar(o) {
    if (!o.trigger || !o.panel) return null;
    var view = null, focusIso = null;
    var pop = makePopover(o.trigger, o.panel, function () {
      var seed = isoParts(o.value() && inRange(o.value()) ? o.value() : o.min());
      view = { y: seed.y, m: seed.m };
      focusIso = null;
      render();
    });
    function inRange(iso) { return iso >= o.min() && iso <= o.max(); }
    function sameMonth(iso) { var p = isoParts(iso); return p.y === view.y && p.m === view.m; }
    function render() {
      var y = view.y, m = view.m;
      var first = new Date(Date.UTC(y, m, 1)).getUTCDay();
      var dim = new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
      var sel = o.value(), today = nowIST().iso;
      var ym = isoOf(y, m, 1).slice(0, 7);
      if (!focusIso || !sameMonth(focusIso)) {
        focusIso = null;
        [sel, today].forEach(function (c) { if (!focusIso && c && inRange(c) && sameMonth(c)) focusIso = c; });
        for (var d0 = 1; !focusIso && d0 <= dim; d0++) if (inRange(isoOf(y, m, d0))) focusIso = isoOf(y, m, d0);
      }
      var h = '<div class="bsl-dp__nav">' +
        '<button type="button" class="bsl-dp__navbtn" data-cal-prev aria-label="Previous month"' + (ym <= o.min().slice(0, 7) ? ' disabled' : '') + '>' + CHEV_L + '</button>' +
        '<span class="bsl-dp__month" aria-live="polite">' + CAL_MONTHS[m] + ' ' + y + '</span>' +
        '<button type="button" class="bsl-dp__navbtn" data-cal-next aria-label="Next month"' + (ym >= o.max().slice(0, 7) ? ' disabled' : '') + '>' + CHEV_R + '</button>' +
        '</div><table class="bsl-dp__grid" role="grid"><thead><tr>';
      for (var w = 0; w < 7; w++) h += '<th scope="col" abbr="' + CAL_DOW_FULL[w] + '">' + CAL_DOW[w] + '</th>';
      h += '</tr></thead><tbody><tr>';
      var col = 0, i;
      for (i = 0; i < first; i++, col++) h += '<td></td>';
      for (var d = 1; d <= dim; d++, col++) {
        if (col && col % 7 === 0) h += '</tr><tr>';
        var iso = isoOf(y, m, d), off = !inRange(iso);
        h += '<td class="bsl-dp__cell' + (iso === today ? ' bsl-dp__cell--today' : '') + '"><button type="button" role="gridcell" data-cal-day data-iso="' + iso + '"' +
          ' tabindex="' + (iso === focusIso ? '0' : '-1') + '"' + (off ? ' aria-disabled="true" disabled' : '') +
          ' aria-selected="' + (iso === sel ? 'true' : 'false') + '" aria-label="' + fmtDayFull(iso) + '">' + d + '</button></td>';
      }
      while (col % 7) { h += '<td></td>'; col++; }
      h += '</tr></tbody></table>';
      o.panel.innerHTML = h;
    }
    function focusDay(iso) {
      focusIso = iso;
      if (!sameMonth(iso)) { var p = isoParts(iso); view = { y: p.y, m: p.m }; render(); }
      var el = o.panel.querySelector('[data-iso="' + iso + '"]');
      if (el) el.focus();
    }
    function month(n) {
      var dt = new Date(Date.UTC(view.y, view.m + n, 1));
      var ym = isoOf(dt.getUTCFullYear(), dt.getUTCMonth(), 1).slice(0, 7);
      if (ym < o.min().slice(0, 7) || ym > o.max().slice(0, 7)) return;
      view = { y: dt.getUTCFullYear(), m: dt.getUTCMonth() };
      focusIso = null;
      render();
      pop.focusFirst();
    }
    o.panel.addEventListener('click', function (e) {
      var b = e.target.closest('button');
      if (!b) return;
      if (b.hasAttribute('data-cal-day')) { if (b.disabled) return; pop.hide(); o.onPick(b.getAttribute('data-iso')); }
      else if (b.hasAttribute('data-cal-prev')) month(-1);
      else if (b.hasAttribute('data-cal-next')) month(1);
    });
    o.panel.addEventListener('keydown', function (e) {
      var k = e.key;
      if (k === 'PageUp' || k === 'PageDown') { e.preventDefault(); month(k === 'PageUp' ? -1 : 1); return; }
      var a = document.activeElement;
      if (!a || !a.hasAttribute('data-cal-day')) return;
      var delta = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 }[k];
      if (k === 'Home') delta = -dowOf(focusIso);
      if (k === 'End') delta = 6 - dowOf(focusIso);
      if (delta === undefined) return;
      e.preventDefault();
      var next = addDays(focusIso, delta);
      if (inRange(next)) focusDay(next);
    });
    return pop;
  }

  /* o: { trigger, panel, value() -> minutes|null, ok(minutes) -> bool, onPick(minutes),
         quick() -> { label, min } | null } */
  function makeClock(o) {
    if (!o.trigger || !o.panel) return null;
    var view = null;   // { mode: 'hour'|'minute', h: 0-23, m: minute|null }
    function hourOk(h) { for (var t = 0; t < 60; t += 5) if (o.ok(h * 60 + t)) return true; return false; }
    function halfOk(pm) { for (var h = pm ? 12 : 0; h < (pm ? 24 : 12); h++) if (hourOk(h)) return true; return false; }
    function firstOk() { for (var x = 0; x < DAY_MIN; x += 5) if (o.ok(x)) return x; return 0; }
    var pop = makePopover(o.trigger, o.panel, function () {
      var v = o.value();
      if (v == null || !o.ok(v)) v = firstOk();
      view = { mode: 'hour', h: Math.floor(v / 60), m: v % 60 };
      render();
    });
    function render() {
      var pm = view.h >= 12, h12 = view.h % 12 || 12, i, items = [];
      var h = '<div class="bsl-clk__head" aria-live="polite">' +
        '<button type="button" class="bsl-clk__seg" data-clk-seg="hour" aria-current="' + (view.mode === 'hour') + '" aria-label="Hour ' + h12 + '">' + h12 + '</button>' +
        '<span class="bsl-clk__colon" aria-hidden="true">:</span>' +
        '<button type="button" class="bsl-clk__seg" data-clk-seg="minute" aria-current="' + (view.mode === 'minute') + '" aria-label="Minutes ' + pad(view.m || 0) + '">' + pad(view.m || 0) + '</button>' +
        '<span class="bsl-clk__ampm">' +
        '<button type="button" class="bsl-clk__ampmbtn" data-clk-ampm="AM" aria-pressed="' + !pm + '"' + (halfOk(false) ? '' : ' disabled') + '>AM</button>' +
        '<button type="button" class="bsl-clk__ampmbtn" data-clk-ampm="PM" aria-pressed="' + pm + '"' + (halfOk(true) ? '' : ' disabled') + '>PM</button>' +
        '</span></div>';
      for (i = 0; i < 12; i++) {
        if (view.mode === 'hour') {
          var v12 = i === 0 ? 12 : i, h24 = (v12 % 12) + (pm ? 12 : 0);
          items.push({ val: v12, label: String(v12), aria: v12 + ' o’clock ' + (pm ? 'PM' : 'AM'), off: !hourOk(h24), sel: h24 === view.h });
        } else {
          items.push({ val: i * 5, label: pad(i * 5), aria: h12 + ':' + pad(i * 5) + (pm ? ' PM' : ' AM'), off: !o.ok(view.h * 60 + i * 5), sel: view.m === i * 5 });
        }
      }
      var focus = -1;
      items.forEach(function (it, j) { if (focus < 0 && it.sel && !it.off) focus = j; });
      items.forEach(function (it, j) { if (focus < 0 && !it.off) focus = j; });
      h += '<div class="bsl-clk__dial" data-mode="' + view.mode + '">';
      var selIdx = -1;
      items.forEach(function (it, j) {
        var p = dialPos(j);
        if (it.sel) selIdx = j;
        h += '<button type="button" class="bsl-clk__pos" data-clk-pos data-value="' + it.val + '" style="left:' + p.x + 'px;top:' + p.y + 'px"' +
          ' tabindex="' + (j === focus ? '0' : '-1') + '"' + (it.off ? ' aria-disabled="true" disabled' : '') +
          ' aria-selected="' + it.sel + '" aria-label="' + it.aria + '">' + it.label + '</button>';
      });
      if (selIdx > -1) h += '<span class="bsl-clk__hand" aria-hidden="true" style="transform:rotate(' + (selIdx * 30) + 'deg)"></span>';
      h += '<span class="bsl-clk__pin" aria-hidden="true"></span></div>';
      var q = o.quick ? o.quick() : null;
      h += '<p class="bsl-clk__hint">' + (view.mode === 'hour' ? 'Pick the hour' : 'Now pick the minutes') + '</p>';
      if (q) h += '<div class="bsl-clk__foot"><button type="button" class="bsl-clk__quick" data-clk-quick="' + q.min + '">' + escHtml(q.label) + '</button></div>';
      o.panel.innerHTML = h;
    }
    function refocus() { var f = o.panel.querySelector('[data-clk-pos][tabindex="0"]'); if (f) f.focus(); }
    o.panel.addEventListener('click', function (e) {
      var b = e.target.closest('button');
      if (!b || b.disabled) return;
      if (b.hasAttribute('data-clk-seg')) { view.mode = b.getAttribute('data-clk-seg'); render(); refocus(); }
      else if (b.hasAttribute('data-clk-ampm')) {
        var pm = b.getAttribute('data-clk-ampm') === 'PM';
        var nh = (view.h % 12) + (pm ? 12 : 0);
        if (!hourOk(nh)) for (var x = pm ? 12 : 0; x < (pm ? 24 : 12); x++) if (hourOk(x)) { nh = x; break; }
        view.h = nh;
        view.mode = 'hour';
        render(); refocus();
      } else if (b.hasAttribute('data-clk-pos')) {
        var val = parseInt(b.getAttribute('data-value'), 10);
        if (view.mode === 'hour') {
          view.h = (val % 12) + (view.h >= 12 ? 12 : 0);
          if (view.m == null || !o.ok(view.h * 60 + view.m)) view.m = null;
          view.mode = 'minute';
          render(); refocus();
        } else {
          pop.hide();
          o.onPick(view.h * 60 + val);
        }
      } else if (b.hasAttribute('data-clk-quick')) {
        pop.hide();
        o.onPick(parseInt(b.getAttribute('data-clk-quick'), 10));
      }
    });
    o.panel.addEventListener('keydown', function (e) {
      var pos = Array.prototype.slice.call(o.panel.querySelectorAll('[data-clk-pos]'));
      var i = pos.indexOf(document.activeElement);
      if (i < 0) return;
      var d = (e.key === 'ArrowLeft' || e.key === 'ArrowUp') ? -1 : (e.key === 'ArrowRight' || e.key === 'ArrowDown') ? 1 : 0;
      if (!d) return;
      e.preventDefault();
      for (var s = 1, n = pos.length; s <= n; s++) {
        var j = (i + d * s + n * 2) % n;
        if (!pos[j].disabled) { pos[i].setAttribute('tabindex', '-1'); pos[j].setAttribute('tabindex', '0'); pos[j].focus(); return; }
      }
    });
    return pop;
  }

  /* ======================= BOOKING FLOW ======================= */

  /* ======================= LOCATION PICKER (pickup & return) =======================
     Ported from the shipping form's pickup picker (initPickup in
     sections/arivah_checkout_step_1.liquid): Places (New) autocomplete with session
     tokens, live location, a draggable pin and reverse lookup through Places
     searchNearby. Added here: the service circle and a straight-line distance check.
     Every part is additive. Without a key, or if Google Maps can't load, the search box
     is a plain text field and the booking still completes (staff confirm the spot). */

  function haversineKm(a, b) {
    if (!a || !b || a.lat == null || b.lat == null) return Infinity;
    var R = 6371, toR = Math.PI / 180;
    var dLat = (b.lat - a.lat) * toR, dLng = (b.lng - a.lng) * toR;
    var s = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(a.lat * toR) * Math.cos(b.lat * toR) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return 2 * R * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
  }
  function kmText(km) { return km < 1 ? Math.round(km * 1000 / 10) * 10 + ' m' : (Math.round(km * 10) / 10) + ' km'; }

  /* Places address components (Places New: longText; Geocoding: long_name) to the
     form's street & area, city and PIN. The street number is left out on purpose: the
     nearest-place lookup behind a map tap can return a neighbour's number. Same rules
     as the shipping form's pickup address. */
  function addressParts(components, formatted) {
    var by = {};
    (components || []).forEach(function (c) {
      var v = c.longText || c.long_name || '';
      (c.types || []).forEach(function (t) { if (v && !by[t]) by[t] = v; });
    });
    var city = by.locality || by.postal_town || by.administrative_area_level_3 || by.administrative_area_level_2 || '';
    var bits = [];
    [by.route, by.neighborhood, by.sublocality_level_3, by.sublocality_level_2, by.sublocality_level_1 || by.sublocality].forEach(function (b) {
      if (b && b !== city && bits.indexOf(b) < 0) bits.push(b);
    });
    if (bits.length > 3) bits = [bits[0], bits[1], bits[bits.length - 1]];   // road, local area, main area
    var area = bits.join(', ');
    if (!area && formatted) {
      // "Italgacha Rd, Dum Dum, Kolkata, West Bengal 700079, India": keep what comes before the city.
      var segs = String(formatted).split(',').map(function (x) { return x.trim(); });
      var stop = segs.length;
      for (var i = 0; i < segs.length; i++) {
        if ((city && segs[i] === city) || /\b\d{6}\b/.test(segs[i]) || /^india$/i.test(segs[i])) { stop = i; break; }
      }
      // No city component: the part just before 'State PIN' is the city.
      if (!city && stop >= 2 && stop < segs.length) { city = segs[stop - 1]; stop--; }
      area = segs.slice(0, stop).join(', ');
    }
    var pin = /^\d{6}$/.test(by.postal_code || '') ? by.postal_code : ((String(formatted || '').match(/\b([1-9]\d{5})\b/) || [])[1] || '');
    return { area: area.slice(0, 160), city: city.slice(0, 60), pin: pin };
  }
  /* A search pick of a building, hotel or street address names the place for line one. */
  function placeName(prediction, primary) {
    var t = (prediction && prediction.types) || [];
    var named = ['establishment', 'point_of_interest', 'premise', 'subpremise', 'street_address'].some(function (k) { return t.indexOf(k) > -1; });
    return named ? String(primary || '').slice(0, 120) : '';
  }

  /* One Maps SDK load per page, shared with the shipping form (same global names). */
  function ensureGmaps(key) {
    if (global.google && global.google.maps && global.google.maps.places) return Promise.resolve(global.google.maps);
    if (global.__bsGmaps) return global.__bsGmaps;
    global.__bsGmaps = new Promise(function (resolve, reject) {
      var cbName = '__bsGmapsInit';
      var t = setTimeout(function () { reject(new Error('timeout')); }, 12000);
      global[cbName] = function () { clearTimeout(t); resolve(global.google.maps); };
      var s = document.createElement('script');
      s.src = 'https://maps.googleapis.com/maps/api/js?key=' + encodeURIComponent(key) +
              '&libraries=places&callback=' + cbName + '&v=weekly&region=IN&loading=async';
      s.async = true;
      s.onerror = function () { clearTimeout(t); reject(new Error('load')); };
      document.head.appendChild(s);
    });
    return global.__bsGmaps;
  }
  /* Any RpcError / permission / quota failure is a key or project problem, not a blip. */
  function looksBlocked(err) {
    if (!err) return false;
    if (/xhr error|network|timed? ?out/i.test(String(err.message || ''))) return false;   // a dropped connection is a blip
    if (err.name === 'RpcError') return true;
    var code = err.code;
    if (code === 3 || code === 7 || code === 8 || code === 16) return true;
    return /permission|denied|disabled|quota|blocked|referer|referrer|not authorized|invalid.*key|REQUEST_DENIED|OVER_QUERY_LIMIT/i.test(String(err.message || err || ''));
  }

  /* Google calls window.gm_authFailure when the key is rejected (wrong referrer, billing,
     disabled API). The map still constructs and reports a size, then shows an error panel,
     so this callback is the reliable signal to switch every picker to typed addresses. */
  var AUTH_FAIL = [];
  function hookAuthFailure() {
    if (global.__bslAuthHook) return;
    global.__bslAuthHook = true;
    var prev = global.gm_authFailure;
    global.gm_authFailure = function () {
      try { if (typeof prev === 'function') prev(); } catch (e) { /* keep going */ }
      AUTH_FAIL.forEach(function (fn) { fn(); });
    };
  }

  var LOC_SEQ = 0;
  function makeLocationPicker(o) {
    var el = o.el;
    var input = el.querySelector('[data-bsl-loc-input]');
    var list = el.querySelector('[data-bsl-loc-list]');
    var gpsBtn = el.querySelector('[data-bsl-loc-gps]');
    var mapBox = el.querySelector('[data-bsl-loc-map]');
    var canvas = el.querySelector('[data-bsl-loc-canvas]');
    var badge = el.querySelector('[data-bsl-loc-badge]');
    var SVC = o.service;
    var uid = 'bsl-loc' + (++LOC_SEQ);

    var pick = { lat: null, lng: null, address: '', src: '' };
    var maps = o.key ? null : false;          // null: not tried yet; false: unavailable
    var mp = { map: null, marker: null, circle: null, tiles: false };
    var net = { timer: null, seq: 0, cache: {}, blockedUntil: 0, token: null, lastRev: null };
    var transient = null;                      // short-lived status, e.g. "Finding your location…"
    var items = [], active = -1;
    hookAuthFailure();
    AUTH_FAIL.push(function () { fail(); });

    function km() { return haversineKm(SVC, pick); }
    function state() {
      if (pick.lat != null) return km() <= SVC.km + 1e-9 ? 'ok' : 'outside';
      if (maps === false) return input.value.trim().length >= 4 ? 'typed' : 'none';
      return 'none';
    }
    function paintBadge() {
      if (!badge) return;
      var st = state(), txt = '', tone = '';
      if (transient) { txt = transient.text; tone = transient.tone || ''; }
      else if (st === 'ok') { txt = 'We serve this spot · ' + kmText(km()) + ' from ' + SVC.name; tone = 'ok'; }
      else if (st === 'outside') { txt = 'Heads up: this spot is ' + kmText(km()) + ' from ' + SVC.name + ', outside our usual ' + SVC.km + ' km area. You can still book. We’ll WhatsApp you to confirm the pickup before your agent sets out.'; tone = 'warn'; }
      else if (st === 'typed') { txt = 'We’ll confirm your exact spot on WhatsApp before pickup.'; tone = ''; }
      else if (maps === false) { txt = 'Type your area or street. We serve ' + SVC.km + ' km around ' + SVC.name + '.'; }
      else { txt = 'Search a place, use your location or tap the map. We serve ' + SVC.km + ' km around ' + SVC.name + '.'; }
      badge.textContent = txt;
      if (tone) badge.setAttribute('data-tone', tone); else badge.removeAttribute('data-tone');
      if (mp.marker) mp.marker.setIcon(pinIcon(st === 'outside' ? (o.warn || o.error) : o.accent));
    }
    function say(text, tone, ms) {
      transient = text ? { text: text, tone: tone } : null;
      paintBadge();
      if (text && ms) setTimeout(function () { if (transient && transient.text === text) { transient = null; paintBadge(); } }, ms);
    }
    function changed(why) { transient = null; paintBadge(); if (o.onChange) o.onChange(why); }

    /* ---------- suggestions list (combobox) ---------- */
    function closeList() {
      if (!list) return;
      list.hidden = true; list.innerHTML = ''; items = []; active = -1;
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
    }
    function setActive(i) {
      var opts = list.querySelectorAll('[role="option"]');
      if (!opts.length) return;
      active = (i + opts.length) % opts.length;
      Array.prototype.forEach.call(opts, function (li, k) { li.setAttribute('aria-selected', k === active ? 'true' : 'false'); });
      input.setAttribute('aria-activedescendant', opts[active].id);
      opts[active].scrollIntoView({ block: 'nearest' });
    }
    function renderList(res) {
      items = res || [];
      if (!items.length) { closeList(); return; }
      list.innerHTML = '';
      items.forEach(function (it, i) {
        var li = document.createElement('li');
        li.id = uid + '-o' + i;
        li.setAttribute('role', 'option');
        li.setAttribute('aria-selected', 'false');
        li.className = 'bsl__combo-opt';
        var far = it.meters != null && it.meters / 1000 > SVC.km;
        li.innerHTML = '<span class="bsl__combo-main"></span><span class="bsl__combo-sub"></span>' +
          (it.meters != null ? '<span class="bsl__combo-km' + (far ? ' is-far' : '') + '">' + (far ? kmText(it.meters / 1000) + ' · outside ' + SVC.km + ' km' : kmText(it.meters / 1000)) + '</span>' : '');
        li.querySelector('.bsl__combo-main').textContent = it.primary;
        li.querySelector('.bsl__combo-sub').textContent = it.secondary;
        li.addEventListener('mousedown', function (e) { e.preventDefault(); });
        li.addEventListener('click', function () { choose(i); });
        list.appendChild(li);
      });
      list.hidden = false;
      input.setAttribute('aria-expanded', 'true');
      active = -1;
    }
    function token() {
      if (!net.token) net.token = new global.google.maps.places.AutocompleteSessionToken();
      return net.token;
    }
    function trip(err) {
      if (!net.blockedUntil && global.console && err) console.warn('Google Maps request blocked: check API enablement, quota and key restrictions.', err.message || err);
      net.blockedUntil = Date.now() + (net.blockedUntil ? 300000 : 60000);
    }
    function search(q) {
      if (net.timer) clearTimeout(net.timer);
      if (q.length < 3 || maps === false || Date.now() < net.blockedUntil) { closeList(); return; }
      var key = q.toLowerCase().replace(/\s+/g, ' ');
      if (net.cache[key]) { renderList(net.cache[key]); return; }
      net.timer = setTimeout(function () {
        var my = ++net.seq;
        load().then(function (g) {
          if (!g || my !== net.seq) return null;
          return global.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
            input: q,
            sessionToken: token(),
            includedRegionCodes: ['in'],
            origin: { lat: SVC.lat, lng: SVC.lng },
            locationBias: { center: { lat: SVC.lat, lng: SVC.lng }, radius: Math.max(5000, SVC.km * 2000) }
          });
        }).then(function (res) {
          if (!res || my !== net.seq) return;
          var out = [];
          (res.suggestions || []).forEach(function (sg) {
            var p = sg.placePrediction;
            if (!p) return;
            out.push({ prediction: p, value: p.text.text, primary: (p.mainText && p.mainText.text) || p.text.text,
              secondary: (p.secondaryText && p.secondaryText.text) || '', meters: typeof p.distanceMeters === 'number' ? p.distanceMeters : null });
          });
          net.cache[key] = out;
          if (document.activeElement === input) renderList(out);
        }).catch(function (err) {
          if (looksBlocked(err)) trip(err);
          if (my === net.seq) { closeList(); say('We couldn’t fetch suggestions. Tap the map or use your location instead.', 'error', 6000); }
        });
      }, 320);
    }
    function choose(i) {
      var it = items[i];
      closeList();
      if (!it) return;
      input.value = it.value;
      var place = it.prediction.toPlace();
      place.fetchFields({ fields: ['location', 'formattedAddress', 'addressComponents'] }).then(function () {
        set({ lat: place.location.lat(), lng: place.location.lng(), address: place.formattedAddress || it.value, src: 'Search' }, true);
        if (o.onAddress) o.onAddress(addressParts(place.addressComponents, place.formattedAddress), placeName(it.prediction, it.primary));
        net.token = null;   // the selection closes the billing session
      }).catch(function (err) {
        if (looksBlocked(err)) trip(err);
        say('We couldn’t place that on the map. Tap the map to set the spot.', 'error', 6000);
      });
    }

    /* ---------- map ---------- */
    function pinIcon(color) {
      if (!global.google || !global.google.maps) return null;
      return {
        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 34"><path fill="' + color + '" d="M12 0C5.4 0 0 5.4 0 12c0 9 12 22 12 22s12-13 12-22c0-6.6-5.4-12-12-12z"/><circle cx="12" cy="12" r="4.5" fill="#fff"/></svg>'),
        scaledSize: new global.google.maps.Size(34, 46),
        anchor: new global.google.maps.Point(17, 46)
      };
    }
    function load() {
      if (maps === false) return Promise.resolve(null);
      return ensureGmaps(o.key).then(function (g) { maps = true; return g; }, function () { fail(); return null; });
    }
    function fail() {
      if (maps === false) return;
      maps = false;
      if (mapBox) mapBox.hidden = true;
      if (gpsBtn) gpsBtn.hidden = true;
      closeList();
      transient = null;
      paintBadge();
      if (o.onMaps) o.onMaps(false);
      if (o.onChange) o.onChange('maps');
    }
    function mount() {
      if (mp.map || !canvas || maps !== true) return;
      var G = global.google.maps;
      var centre = { lat: SVC.lat, lng: SVC.lng };
      mp.map = new G.Map(canvas, {
        center: pick.lat != null ? { lat: pick.lat, lng: pick.lng } : centre,
        zoom: 13, gestureHandling: 'cooperative', clickableIcons: false,
        mapTypeControl: false, streetViewControl: false, fullscreenControl: false, zoomControl: true
      });
      mp.circle = new G.Circle({
        map: mp.map, center: centre, radius: SVC.km * 1000, clickable: false,
        strokeColor: o.accent, strokeOpacity: 0.7, strokeWeight: 2, fillColor: o.accent, fillOpacity: 0.07
      });
      new G.Marker({
        map: mp.map, position: centre, clickable: false, zIndex: 1, title: SVC.name,
        icon: {
          url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32"><circle cx="16" cy="16" r="15" fill="#14122b"/><path fill="#fff" d="M24 19.2v-1.6l-6.4-4V9.2a1.2 1.2 0 0 0-2.4 0v4.4l-6.4 4v1.6l6.4-2v4.4l-1.6 1.2V24l2.8-.8 2.8.8v-1.2L17.6 21.6v-4.4z"/></svg>'),
          scaledSize: new G.Size(30, 30), anchor: new G.Point(15, 15)
        }
      });
      mp.marker = new G.Marker({ map: pick.lat != null ? mp.map : null, position: pick.lat != null ? { lat: pick.lat, lng: pick.lng } : centre, draggable: true, zIndex: 2, icon: pinIcon(o.accent) });
      mp.marker.addListener('dragend', function (e) { pinAt(e.latLng.lat(), e.latLng.lng(), false); });
      mp.map.addListener('click', function (e) { pinAt(e.latLng.lat(), e.latLng.lng(), false); });
      if (pick.lat == null) mp.map.fitBounds(mp.circle.getBounds(), 8);
      G.event.addListenerOnce(mp.map, 'tilesloaded', function () { mp.tiles = true; });
      paintBadge();
    }
    /* A map mounted while hidden measures 0x0; keep nudging it until tiles load. Google's
       error panel (bad key, billing) means "maps unavailable", whenever it shows up. */
    function awaitRender(n) {
      n = n || 0;
      if (!mp.map || maps !== true) return;
      if (canvas.querySelector('.gm-err-container')) { fail(); return; }
      if (mp.tiles || n >= 40) return;
      if (canvas.offsetWidth) {
        global.google.maps.event.trigger(mp.map, 'resize');
        if (pick.lat != null) mp.map.setCenter({ lat: pick.lat, lng: pick.lng });
        else mp.map.fitBounds(mp.circle.getBounds(), 8);
      }
      setTimeout(function () { awaitRender(n + 1); }, 150);
    }
    function pinAt(lat, lng, pan, src) {
      input.value = 'Pinned spot · ' + lat.toFixed(5) + ', ' + lng.toFixed(5);
      set({ lat: lat, lng: lng, address: '', src: src || 'Map pin' }, pan);
      reverse(lat, lng);
    }
    function reverse(lat, lng) {
      if (maps !== true || Date.now() < net.blockedUntil) return;
      var here = { lat: lat, lng: lng };
      if (net.lastRev && haversineKm(net.lastRev, here) < 0.025) return;
      net.lastRev = here;
      var my = ++net.seq;
      function attempt(radius) {
        fetch('https://places.googleapis.com/v1/places:searchNearby', {
          method: 'POST', referrerPolicy: 'origin',
          headers: { 'Content-Type': 'application/json', 'X-Goog-Api-Key': o.key, 'X-Goog-FieldMask': 'places.formattedAddress,places.addressComponents' },
          body: JSON.stringify({ locationRestriction: { circle: { center: { latitude: lat, longitude: lng }, radius: radius } }, maxResultCount: 1, rankPreference: 'DISTANCE' })
        }).then(function (r) { return r.json(); }).then(function (j) {
          if (j.error) { trip({ message: j.error.message }); return; }
          var p = (j.places || [])[0];
          if (!p && radius < 1200) { attempt(1200); return; }
          if (my !== net.seq || pick.lat !== lat || pick.lng !== lng) return;
          if (p && p.formattedAddress) {
            pick.address = p.formattedAddress;
            input.value = 'Near ' + p.formattedAddress;
            changed('address');
            if (o.onAddress) o.onAddress(addressParts(p.addressComponents, p.formattedAddress), '');
          }
        }).catch(function () {});
      }
      attempt(300);
    }

    /* ---------- public ---------- */
    function set(p, pan) {
      pick = { lat: p.lat == null ? null : +p.lat, lng: p.lng == null ? null : +p.lng, address: p.address || '', src: p.src || '' };
      if (p.address && !p.keepInput) input.value = p.address;
      if (mp.marker) {
        if (pick.lat != null) {
          mp.marker.setPosition({ lat: pick.lat, lng: pick.lng });
          mp.marker.setMap(mp.map);
          if (pan) { mp.map.panTo({ lat: pick.lat, lng: pick.lng }); if (mp.map.getZoom() < 14) mp.map.setZoom(14); }
        } else mp.marker.setMap(null);
      }
      changed('pick');
    }
    function activate() {
      if (maps === false) { paintBadge(); return; }
      load().then(function (g) {
        if (!g) return;
        if (mapBox) mapBox.hidden = false;
        if (gpsBtn && navigator.geolocation && global.isSecureContext) gpsBtn.hidden = false;
        mount();
        requestAnimationFrame(function () { awaitRender(0); });
        if (o.onMaps) o.onMaps(true);
      });
    }

    /* ---------- events ---------- */
    input.addEventListener('input', function () {
      // A pin that no longer matches the typed text is worse than none.
      if (pick.lat != null && input.value !== pick.address && input.value !== 'Near ' + pick.address) {
        pick = { lat: null, lng: null, address: '', src: '' };
        if (mp.marker) mp.marker.setMap(null);
      }
      if (!input.value.trim()) net.token = null;
      changed('typing');
      search(input.value.trim());
    });
    input.addEventListener('focus', function () { if (maps !== false) load(); });
    input.addEventListener('blur', function () { setTimeout(closeList, 120); net.token = null; });
    input.addEventListener('keydown', function (e) {
      if (list.hidden) return;
      if (e.key === 'ArrowDown') { e.preventDefault(); setActive(active + 1); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(active - 1); }
      else if (e.key === 'Enter' && active > -1) { e.preventDefault(); choose(active); }
      else if (e.key === 'Escape') { e.preventDefault(); closeList(); }
    });
    if (gpsBtn) gpsBtn.addEventListener('click', function () {
      if (!navigator.geolocation) return;
      gpsBtn.setAttribute('data-busy', '');
      say('Finding your location…');
      var settled = false;
      function done() { settled = true; gpsBtn.removeAttribute('data-busy'); }
      var bail = setTimeout(function () { if (!settled) { done(); say('We couldn’t get your location. Search or tap the map instead.', 'error', 6000); } }, 12000);
      navigator.geolocation.getCurrentPosition(function (pos) {
        clearTimeout(bail); done();
        pinAt(pos.coords.latitude, pos.coords.longitude, true, 'Live location');
      }, function (err) {
        clearTimeout(bail); done();
        say(err && err.code === 1 ? 'Location is switched off. Search or tap the map instead.' : 'We couldn’t get your location. Search or tap the map instead.', 'error', 6000);
      }, { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 });
    });

    paintBadge();
    return {
      input: input,
      state: state,
      km: km,
      mapsOn: function () { return maps; },
      get: function () { return { lat: pick.lat, lng: pick.lng, address: pick.address, src: pick.src, text: input.value.trim() }; },
      set: function (p) { if (p && p.text) input.value = p.text; set({ lat: p && p.lat, lng: p && p.lng, address: p && p.address, src: p && p.src, keepInput: true }, true); },
      setPin: function (lat, lng, address) { input.value = address || ('Pinned spot · ' + lat.toFixed(5) + ', ' + lng.toFixed(5)); set({ lat: lat, lng: lng, address: address || '', src: 'Map pin' }, true); },
      pinAt: function (lat, lng) { pinAt(lat, lng, true, 'Map pin'); },
      activate: activate,
      paint: paintBadge
    };
  }

  var SHIP_ATTRS = ['Flight From', 'Flight To', 'Flight To Code', 'Flight Date', 'PNR', 'Pickup point',
    'Pickup map link', 'Pickup pincode', 'Pickup address', 'Pickup coordinates', 'Pickup location source', 'Pickup Type',
    'Pickup Date', 'Pickup Time', 'Total weight', 'Bag 1', 'Fragile bags', 'Delivery Type', 'Subtotal',
    'GST (18%)', 'Quoted total', 'Name', 'Mobile', 'WhatsApp updates', 'Deliver to name', 'Deliver to mobile', 'Delivery address',
    'Delivery landmark', 'Delivery pincode', 'Delivery city', 'Delivery state'];

  function init(root) {
    if (root.__bsl) return;
    root.__bsl = true;
    /* A mobile (WhatsApp code) sign-in pre-fills name and mobile when Shopify's account didn't. */
    function prefillFromLogin() {
      var p = window.BSLogin && window.BSLogin.profile();
      if (!p) return;
      var n = root.querySelector('[data-bsl-name]'), ph = root.querySelector('[data-bsl-phone]');
      if (n && !n.value && p.name) { n.value = p.name; n.dispatchEvent(new Event('input', { bubbles: true })); }
      if (ph && !ph.value && p.mobile) { ph.value = p.mobile; ph.dispatchEvent(new Event('input', { bubbles: true })); }
    }
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', prefillFromLogin); else setTimeout(prefillFromLogin, 0);
    var cfgEl = root.querySelector('[data-bsl-config]');
    if (!cfgEl) return;
    var cfg;
    try { cfg = JSON.parse(cfgEl.textContent); } catch (e) { return; }
    cfg.sizes = {};
    cfg.handling = {};
    cfg.handlingOne = {};
    cfg.handlingHub = {};
    (cfg.storage.variants || []).forEach(function (v) {
      var si = cfg.storage.sizes.indexOf(v.o1), pi = cfg.storage.plans.indexOf(v.o2);
      if (si < 0 || pi < 0 || !v.a) return;
      var key = SIZE_KEYS[si];
      cfg.sizes[key] = cfg.sizes[key] || { name: (cfg.sizeNames && cfg.sizeNames[si]) || v.o1, weight: (cfg.sizeWeights && cfg.sizeWeights[si]) || '', v: {} };
      cfg.sizes[key].v[PLAN_KEYS[pi]] = { id: v.id, p: v.p };
    });
    var hp = cfg.handlingProduct || {};
    (hp.variants || []).forEach(function (v) {
      var si = (hp.sizes || []).indexOf(v.o1);
      if (si < 0 || !v.a) return;
      cfg.handling[SIZE_KEYS[si]] = { id: v.id, p: v.p };
    });
    var ho = cfg.handlingOneway || {};
    (ho.variants || []).forEach(function (v) {
      var si = (ho.sizes || []).indexOf(v.o1);
      if (si < 0 || !v.a) return;
      cfg.handlingOne[SIZE_KEYS[si]] = { id: v.id, p: v.p };
    });
    var hh = cfg.handlingHubProduct || {};
    (hh.variants || []).forEach(function (v) {
      var si = (hh.sizes || []).indexOf(v.o1);
      if (si < 0 || !v.a) return;
      cfg.handlingHub[SIZE_KEYS[si]] = { id: v.id, p: v.p };
    });
    /* The flat handling part (pre-GST paise) every booking pays, from the hub-only rate. */
    function hubBase(key) { var h = cfg.handlingHub[key]; return h ? Math.round(h.p / (1 + (cfg.gst || 0) / 100)) : 0; }
    var GST_DIV = 1 + (cfg.gst || 0) / 100;
    function handlingBase(key) { var hv = cfg.handling[key]; return hv ? Math.round(hv.p / GST_DIV) : 0; }

    var OPEN = cfg.allDay ? 0 : toMin(cfg.open);
    var CLOSE = cfg.allDay ? DAY_MIN : toMin(cfg.close);
    var LEAD = cfg.lead || 30;
    var MAX_H = (cfg.maxDays || 7) * 24;
    var PANELS = 4;   // 1 bags · 2 time · 3 pickup & return · 4 details & pay

    function $(sel) { return root.querySelector(sel); }
    function $$(sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); }

    var st = {
      step: 1,
      counts: { p: 0, c: 0, k: 0 },
      day: 'today', date: '', time: '',
      dur: '4', hours: 4,
      durMode: 'len', until: '', untilBad: false,
      pickup: { line: '', landmark: '', area: '', pin: '' }, drop: { line: '', landmark: '', area: '', pin: '' },
      dropSame: true, note: '', tab: 'pickup',
      // Who travels each leg: 'agent' (default) or 'self' (the customer comes to the hub).
      // 'drop' is the return leg throughout this file.
      legs: { pickup: 'agent', drop: 'agent' },
      name: '', phone: '',
      protect: false
    };

    /* ---------- drop-off date and time ---------- */
    // Any 5-minute time from the earliest bookable minute (now + lead, today only)
    // to the last drop-off of the day (24/7: 11:55 PM; fixed hours: closing - 30 min).
    var LAST_DROP = cfg.allDay ? DAY_MIN - 5 : CLOSE - 30;
    function minFor(iso) {
      var n = nowIST();
      if (!iso || iso < n.iso) return Infinity;
      if (iso > n.iso) return OPEN;
      return Math.max(OPEN, Math.ceil((n.min + LEAD) / 5) * 5);
    }
    function timeOk(iso, m) { return m % 5 === 0 && m >= minFor(iso) && m <= LAST_DROP; }
    function dayOpen(iso) { return minFor(iso) <= LAST_DROP; }
    function dropIso() {
      var today = nowIST().iso;
      if (st.day === 'today') return today;
      if (st.day === 'tomorrow') return addDays(today, 1);
      return st.date;
    }
    function dropAbs() {
      var iso = dropIso();
      return iso && st.time !== '' ? absOf(iso, parseInt(st.time, 10)) : null;
    }

    var otherWrap = $('[data-bsl-other]');
    var dateBtn = $('[data-bsl-date]');
    var timeBtn = $('[data-bsl-time]');
    var timeTouched = false; // the customer picked a time (vs our suggestion)
    var DEFAULT_MIN = 600;   // 10:00 AM: first suggestion on a day that isn't today
    function setBtnText(btn, text, empty) {
      if (!btn) return;
      var t = btn.querySelector('[data-bsl-btn-text]');
      if (t) t.textContent = text;
      btn.toggleAttribute('data-empty', !!empty);
    }

    function paintDays() {
      var today = nowIST().iso;
      var todayRadio = $('[data-bsl-day][value="today"]');
      var todayOpen = dayOpen(today);
      if (todayRadio) {
        todayRadio.disabled = !todayOpen;
        var lbl = todayRadio.closest('label');
        if (lbl) lbl.toggleAttribute('data-disabled', !todayOpen);
        if (!todayOpen && st.day === 'today') st.day = 'tomorrow';
      }
      $$('[data-bsl-day]').forEach(function (r) { r.checked = r.value === st.day; });
      var tl = $('[data-bsl-day-date="tomorrow"]');
      if (tl) tl.textContent = fmtDate(addDays(today, 1));
      var tdl = $('[data-bsl-day-date="today"]');
      if (tdl) tdl.textContent = todayOpen ? 'From ' + fmtTime(minFor(today)) : (cfg.allDay ? 'No times left' : 'Closed for today');
      if (otherWrap) otherWrap.hidden = st.day !== 'other';
      setBtnText(dateBtn, st.date ? fmtDateLong(st.date) : 'Pick a date', !st.date);
    }

    /* Drop-off time: an analog clock (see makeClock). Today suggests the earliest
       bookable time; any other day 10:00 AM, until the customer picks one. */
    function paintTimes() {
      var iso = dropIso();
      var keep = st.time === '' ? NaN : parseInt(st.time, 10);
      if (!iso || !timeOk(iso, keep)) {
        var pref = '';
        if (iso && dayOpen(iso)) pref = iso === nowIST().iso ? minFor(iso) : (timeOk(iso, DEFAULT_MIN) ? DEFAULT_MIN : minFor(iso));
        st.time = pref === '' ? '' : String(pref);
      }
      setBtnText(timeBtn, st.time === '' ? (iso ? 'No times left on this day' : 'Pick a date first') : fmtTime(parseInt(st.time, 10)), st.time === '');
    }

    var dropCal = makeCalendar({
      trigger: dateBtn,
      panel: $('[data-bsl-date-panel]'),
      min: function () { return nowIST().iso; },
      max: function () { return addDays(nowIST().iso, 60); },
      value: function () { return st.date; },
      onPick: function (iso) {
        st.date = iso;
        if (!timeTouched) st.time = '';
        clearErr('date');
        render();
      }
    });
    var dropClock = makeClock({
      trigger: timeBtn,
      panel: $('[data-bsl-time-panel]'),
      value: function () { return st.time === '' ? null : parseInt(st.time, 10); },
      ok: function (m) { var iso = dropIso(); return !!iso && timeOk(iso, m); },
      onPick: function (m) { st.time = String(m); timeTouched = true; clearErr('time'); render(); },
      quick: function () {
        var iso = dropIso();
        if (iso !== nowIST().iso || !dayOpen(iso)) return null;
        return { label: 'Earliest · ' + fmtTime(minFor(iso)), min: minFor(iso) };
      }
    });

    /* "Until a time" mode: the collection time is the anchor, so moving the drop-off
       recomputes the hours. Charged in whole hours, up to MAX_H. */
    var untilDateBtn = $('[data-bsl-until-date]');
    var untilTimeBtn = $('[data-bsl-until-time]');
    var untilInput = untilTimeBtn;   // focus target for errors
    function untilMin() { return /T\d{2}:\d{2}/.test(st.until) ? toMin(st.until.slice(11, 16)) : null; }
    var untilCal = makeCalendar({
      trigger: untilDateBtn,
      panel: $('[data-bsl-until-date-panel]'),
      min: function () { return dropIso() || nowIST().iso; },
      max: function () { var d = dropAbs(); return d == null ? addDays(nowIST().iso, 7) : isoOfAbs(d + MAX_H * 60); },
      value: function () { return st.until.slice(0, 10); },
      onPick: function (iso) {
        var m = untilMin();
        st.until = iso + 'T' + (m == null ? '12:00' : pad(Math.floor(m / 60)) + ':' + pad(m % 60));
        clearErr('dur');
        render();
        // The kept time may not fit the new day (e.g. before drop-off): go straight to the clock.
        if (st.untilBad && untilClock) setTimeout(untilClock.show, 0);
      }
    });
    var untilClock = makeClock({
      trigger: untilTimeBtn,
      panel: $('[data-bsl-until-time-panel]'),
      value: untilMin,
      ok: function (m) {
        var d = dropAbs();
        if (d == null || !st.until) return false;
        var span = absOf(st.until.slice(0, 10), m) - d;
        return m % 5 === 0 && span >= 60 && span <= MAX_H * 60 && (cfg.allDay || (m >= OPEN && m <= CLOSE));
      },
      onPick: function (m) {
        st.until = st.until.slice(0, 10) + 'T' + pad(Math.floor(m / 60)) + ':' + pad(m % 60);
        clearErr('dur');
        render();
      },
      quick: null
    });
    var PRESETS = ['1', '2', '4', '6', '9', '12', '24', '48'];
    function presetFor(h) { return PRESETS.indexOf(String(h)) > -1 ? String(h) : 'custom'; }
    function syncUntil() {
      st.untilBad = false;
      if (st.durMode !== 'until') return;
      var iso = dropIso();
      if (!iso || st.time === '') return;
      var d = absOf(iso, parseInt(st.time, 10));
      if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(st.until)) { st.until = dtLocal(d + st.hours * 60); return; }
      var h = Math.ceil((absOf(st.until.slice(0, 10), toMin(st.until.slice(11, 16))) - d) / 60);
      if (h < 1) { st.untilBad = 'before'; return; }
      if (h > MAX_H) { st.untilBad = 'max'; return; }
      st.hours = h;
      st.dur = presetFor(h);
    }

    /* ---------- collection ---------- */
    function collection() {
      var iso = dropIso();
      if (!iso || st.time === '') return null;
      // "Until a time": collection is the time the customer chose; whole hours are charged.
      if (st.durMode === 'until' && !st.untilBad && untilMin() != null) {
        return { iso: st.until.slice(0, 10), min: untilMin(), dropIso: iso, dropMin: parseInt(st.time, 10) };
      }
      var abs = parseInt(st.time, 10) + st.hours * 60;
      var dayOff = Math.floor(abs / DAY_MIN);
      var min = abs % DAY_MIN;
      return { iso: addDays(iso, dayOff), min: min, dropIso: iso, dropMin: parseInt(st.time, 10) };
    }
    function collectionOk(c) {
      if (!c || cfg.allDay) return true;
      return c.min >= OPEN && c.min <= CLOSE;
    }

    /* ---------- bags ---------- */
    function paintRates() {
      $$('[data-bsl-rate]').forEach(function (el) {
        var size = cfg.sizes[el.getAttribute('data-bsl-rate')];
        if (!size || !size.v.h1) return;
        var div = 1 + (cfg.gst || 0) / 100;
        var ex = function (v) { return money(Math.round(v.p / div)); };
        // Each price stays on one line with its unit, and "+ GST" never wraps on its own.
        var parts = ['<span class="bsl-nw"><b>' + ex(size.v.h1) + '</b>/hour</span>'];
        if (size.v.half) parts.push('<span class="bsl-nw"><b>' + ex(size.v.half) + '</b> up to 9 h</span>');
        if (size.v.day) parts.push('<span class="bsl-nw"><b>' + ex(size.v.day) + '</b>/day <span class="bsl-rate-gst">+ GST</span></span>');
        else parts[parts.length - 1] = parts[parts.length - 1].replace(/<\/span>$/, ' <span class="bsl-rate-gst">+ GST</span></span>');
        el.innerHTML = parts.join(' · ');
      });
      paintHandle();
    }
    /* Per card: the heaviest bag sets the one handling charge; lighter bags ride free.
       The wording follows the legs chosen in step 3 (agent both ways by default). */
    function paintHandle() {
      var top = topClass(), n = agentLegs();
      var trip = n === 2 ? 'pickup &amp; return' : isSelf('pickup') ? 'the return trip' : 'the pickup trip';
      var Trip = trip.charAt(0).toUpperCase() + trip.slice(1);
      $$('[data-bsl-handle]').forEach(function (el) {
        var key = el.getAttribute('data-bsl-handle');
        var b = handlingBase(key);
        el.hidden = !b;
        if (!b) return;
        if (!n) { el.innerHTML = 'Only <b>' + money(hubBase(key) || b) + '</b> handling · you drop off and collect at the hub'; return; }
        var rank = SIZE_KEYS.indexOf(key), topRank = top ? SIZE_KEYS.indexOf(top) : -1;
        if (top && rank < topRank) el.innerHTML = Trip + ' <b>covered</b> by your ' + money(handlingBase(top)) + ' charge';
        else if (top && rank > topRank) el.innerHTML = 'With this bag, ' + trip + ' is <b>' + money(b) + '</b> per booking (not ' + money(handlingBase(top)) + ')';
        else el.innerHTML = '+ <b>' + money(b) + '</b> ' + (n === 2 ? 'pickup, return' : trip.replace('the ', '')) + ' &amp; handling · once per booking';
      });
    }
    function paintBags() {
      $$('[data-bsl-size]').forEach(function (row) {
        var key = row.getAttribute('data-bsl-size');
        var n = st.counts[key] || 0;
        row.querySelector('[data-bsl-count]').textContent = n;
        row.querySelector('[data-bsl-dec]').disabled = n <= 0;
        row.querySelector('[data-bsl-inc]').disabled = n >= (cfg.maxPerSize || 5);
        row.toggleAttribute('data-active', n > 0);
      });
    }

    /* ---------- duration ---------- */
    var hoursInput = $('[data-bsl-hours]');
    var customWrap = $('[data-bsl-custom]');
    /* Storage-only price (pre-GST base and GST-inclusive) of the chosen bags for h hours. */
    function storageFor(h) {
      var r = { base: 0, total: 0 };
      quote(cfg, { counts: st.counts, hours: h }).lines.forEach(function (l) {
        if (l.kind === 'storage') { r.base += l.base; r.total += l.amount; }
      });
      return r;
    }
    function anyBags() { return (st.counts.p + st.counts.c + st.counts.k) > 0; }

    function paintDuration() {
      var until = st.durMode === 'until';
      $$('[data-bsl-durmode]').forEach(function (r) { r.checked = r.value === st.durMode; });
      var lenWrap = $('[data-bsl-len-wrap]'), untilWrap = $('[data-bsl-until-wrap]');
      if (lenWrap) lenWrap.hidden = until;
      if (untilWrap) untilWrap.hidden = !until;
      $$('[data-bsl-dur]').forEach(function (r) { r.checked = r.value === st.dur; });
      if (customWrap) customWrap.hidden = until || st.dur !== 'custom';
      if (hoursInput) {
        hoursInput.max = MAX_H;
        if (document.activeElement !== hoursInput) hoursInput.value = st.hours;
      }
      var eq = $('[data-bsl-hours-eq]');
      if (eq) eq.textContent = st.hours >= 24 ? '= ' + fmtHours(st.hours) : '';

      // Live price on each duration chip for the bags chosen so far.
      var any = anyBags();
      $$('[data-bsl-dur-price]').forEach(function (el) {
        el.textContent = any ? money(storageFor(parseInt(el.getAttribute('data-bsl-dur-price'), 10)).base) : '';
      });
      var dn = $('[data-bsl-dur-note]');
      if (dn) dn.hidden = !any || until;

      var um = untilMin();
      setBtnText(untilDateBtn, st.until ? fmtDateLong(st.until.slice(0, 10)) : 'Pick a date', !st.until);
      setBtnText(untilTimeBtn, um == null ? 'Pick a time' : fmtTime(um), um == null);

      var out = $('[data-bsl-collect]');
      var fix = $('[data-bsl-fix]');
      var c = collection();
      if (!out) return;
      if (!c || st.untilBad) { out.innerHTML = ''; if (fix) fix.hidden = true; return; }
      out.innerHTML =
        '<div class="bsl__jend"><small>Pickup</small><strong>' + fmtDate(c.dropIso) + '</strong><span>' + fmtTime(c.dropMin) + '</span></div>' +
        '<div class="bsl__jmid"><span class="bsl__jdur">' + fmtHours(st.hours) + '</span></div>' +
        '<div class="bsl__jend bsl__jend--end"><small>Return</small><strong>' + fmtDate(c.iso) + '</strong><span>' + fmtTime(c.min) + '</span></div>';
      var ok = collectionOk(c);
      out.toggleAttribute('data-warn', !ok);
      if (fix) {
        fix.hidden = ok;
        if (!ok) buildFix(c, fix);
      }
    }

    /* Collection lands outside opening hours: offer the two nearest valid choices. */
    function buildFix(c, fix) {
      fix.innerHTML = '';
      var msg = document.createElement('p');
      msg.className = 'bsl__fix-msg';
      msg.textContent = 'Agents work ' + fmtTime(OPEN) + ' – ' + fmtTime(CLOSE % DAY_MIN) + '. Choose a return time inside those hours:';
      fix.appendChild(msg);
      var dropAbs = c.dropMin;
      var collectAbs = dropAbs + st.hours * 60;
      var choices = [];
      // earlier: closing time on the collection day (or the day before)
      var dayStart = Math.floor(collectAbs / DAY_MIN) * DAY_MIN;
      var earlier = c.min > CLOSE ? dayStart + CLOSE : dayStart - DAY_MIN + CLOSE;
      var hEarlier = Math.floor((earlier - dropAbs) / 60);
      if (hEarlier >= 1) choices.push(hEarlier);
      // later: next opening time
      var later = c.min < OPEN ? dayStart + OPEN : dayStart + DAY_MIN + OPEN;
      var hLater = Math.ceil((later - dropAbs) / 60);
      if (hLater <= MAX_H) choices.push(hLater);
      choices.forEach(function (h) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'bsl__fix-btn';
        var abs = dropAbs + h * 60;
        b.textContent = 'Return ' + fmtDate(addDays(c.dropIso, Math.floor(abs / DAY_MIN))) + ', ' + fmtTime(abs % DAY_MIN) + ' (' + fmtHours(h) + ')';
        b.addEventListener('click', function () { setHours(h); });
        fix.appendChild(b);
      });
    }

    /* Set the stay to h hours from either mode. */
    function setHours(h) {
      st.hours = h;
      st.dur = presetFor(h);
      var iso = dropIso();
      if (st.durMode === 'until' && iso && st.time !== '') st.until = dtLocal(absOf(iso, parseInt(st.time, 10)) + h * 60);
      clearErr('dur');
      render();
    }

    /* Smart tip: when a cap already covers more time, say so and offer it in one tap. */
    function paintTip() {
      var tip = $('[data-bsl-tip]');
      if (!tip) return;
      var c = collection();
      if (!anyBags() || !c || st.untilBad) { tip.hidden = true; return; }
      var now = storageFor(st.hours).total;
      var h = st.hours;
      while (h + 1 <= MAX_H && storageFor(h + 1).total === now) h++;
      if (h === st.hours) { tip.hidden = true; return; }
      var abs = absOf(c.dropIso, c.dropMin) + h * 60;
      var when = fmtDate(isoOfAbs(abs)) + ', ' + fmtTime(((abs % DAY_MIN) + DAY_MIN) % DAY_MIN);
      tip.hidden = false;
      tip.innerHTML =
        '<span class="bsl__tip-ico" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18h6M10 21h4M12 3a6 6 0 0 0-3.6 10.8c.6.5 1 1.2 1 2V16h5.2v-.2c0-.8.4-1.5 1-2A6 6 0 0 0 12 3Z"/></svg></span>' +
        '<span class="bsl__tip-text">Same price if you keep them up to <strong>' + fmtHours(h) + '</strong>.</span>' +
        '<button type="button" class="bsl__tip-btn" data-bsl-extend="' + h + '">Keep till ' + when + '</button>';
    }

    /* ---------- step 3: pickup & return ---------- */
    var SVC = {
      lat: parseFloat(cfg.service && cfg.service.lat) || 22.653564,
      lng: parseFloat(cfg.service && cfg.service.lng) || 88.445085,
      km: parseFloat(cfg.service && cfg.service.km) || 3,
      name: (cfg.service && cfg.service.name) || 'Kolkata Airport'
    };
    var LEGS = ['pickup', 'drop'];
    var cssVar = function (n, d) { var v = getComputedStyle(root).getPropertyValue(n).trim(); return v || d; };
    var pd = {};
    LEGS.forEach(function (w) {
      var el = $('[data-bsl-loc="' + w + '"]');
      if (!el) return;
      pd[w] = makeLocationPicker({
        el: el, key: cfg.mapsKey || '', service: SVC,
        accent: cssVar('--accent', '#2b1c66'), error: cssVar('--error', '#c0392b'), warn: cssVar('--warn-pin', '#d97706'),
        onChange: function (why) { if (why !== 'typing') clearErr('loc-' + w); else if (pd[w] && pd[w].state() !== 'none') clearErr('loc-' + w); paintPD(); save(); },
        onAddress: function (parts, name) { fillLeg(w, parts, name); }
      });
    });
    function legEl(w, sel) { return $('[data-bsl-loc="' + w + '"] ' + sel); }
    var LEG_NAME = { pickup: 'pickup', drop: 'return' };
    /* The customer handles leg w at the hub themselves. */
    function isSelf(w) { return st.legs[w] === 'self'; }
    function agentLegs() { return (isSelf('pickup') ? 0 : 1) + (isSelf('drop') ? 0 : 1); }
    /* "Return to my pickup address" only means something when an agent does both legs. */
    function sameAddr() { return st.dropSame && !isSelf('pickup') && !isSelf('drop'); }
    /* Handling line for a given choice of legs (null when no bags are picked). */
    function handlingLine(legs) {
      return quote(cfg, { counts: st.counts, hours: st.hours, legs: legs || st.legs }).lines.filter(function (l) { return l.kind === 'handling'; })[0] || null;
    }
    /* What choosing "self" for leg w saves (pre-GST paise), given the other leg as it is. */
    function selfSaving(w) {
      var a = {}, b = {};
      a.pickup = b.pickup = st.legs.pickup; a.drop = b.drop = st.legs.drop;
      a[w] = 'agent'; b[w] = 'self';
      var la = handlingLine(a), lb = handlingLine(b);
      return la && lb ? la.base - lb.base : 0;
    }

    /* Address auto-fill. A filled field carries data-auto (tinted) until the customer
       edits it. A new spot replaces auto values, never text the customer typed. */
    var ADDR_KEYS = { line: 'line', area: 'area', pin: 'pin', landmark: 'landmark' };
    function addrInput(w, k) { return legEl(w, '[data-bsl-addr-field="' + k + '"]'); }
    function syncChip(w) {
      var chip = legEl(w, '[data-bsl-addr-chip]');
      if (!chip) return;
      chip.hidden = !Object.keys(ADDR_KEYS).some(function (k) { var el = addrInput(w, k); return el && el.hasAttribute('data-auto') && el.value.trim(); });
    }
    function setAuto(w, k, v) {
      var el = addrInput(w, k);
      if (!el) return;
      el.value = v;
      st[w][k] = v;
      el.setAttribute('data-auto', '');
      el.removeAttribute('data-flash');
      void el.offsetWidth;   // restart the highlight
      el.setAttribute('data-flash', '');
    }
    function unsetAuto(w, k) {
      var el = addrInput(w, k);
      if (!el || !el.hasAttribute('data-auto')) return;
      el.value = '';
      st[w][k] = '';
      el.removeAttribute('data-auto');
    }
    function fillLeg(w, parts, name) {
      var filled = [];
      ['area', 'pin'].forEach(function (k) {
        if (parts[k]) { setAuto(w, k, parts[k]); filled.push(parts[k]); }
        else unsetAuto(w, k);
      });
      var line = addrInput(w, 'line');
      if (line) {
        if (name && (!line.value.trim() || line.hasAttribute('data-auto'))) { setAuto(w, 'line', name); clearErr('line-' + w); }
        else if (!name) unsetAuto(w, 'line');
      }
      clearErr('pin-' + w);
      syncChip(w);
      var live = legEl(w, '[data-bsl-addr-live]');
      if (live && filled.length) live.textContent = 'Address filled from the map: ' + filled.join(', ') + '.' + (line && !line.value.trim() ? ' Add your house, flat or hotel and room.' : '');
      paintPD(); save();
    }
    /* Outside the service circle: bookable, with a heads-up and a check by the team. */
    function legOutside(w) {
      if (isSelf(w)) return false;
      if (w === 'drop' && sameAddr()) return false;
      return !!pd[w] && pd[w].state() === 'outside';
    }
    function outsideLegs() {
      return LEGS.filter(legOutside).map(function (w) { return { w: w, km: pd[w].km() }; });
    }
    function legState(w) {
      if (isSelf(w)) return true;
      if (w === 'drop' && sameAddr()) return legState('pickup');
      var p = pd[w];
      var placeOk = p ? (p.state() === 'ok' || p.state() === 'outside' || p.state() === 'typed') : true;
      return placeOk && (st[w].line || '').trim().length >= 3;
    }
    function legShort(w) {
      var L = st[w], p = pd[w] ? pd[w].get() : {};
      var t = (L.line || '').trim();
      var where = (L.area || '').trim() || p.address || (p.lat == null ? p.text : '');
      return [t, where].filter(Boolean).join(', ');
    }
    function setTab(w, focus) {
      st.tab = w;
      $$('[data-bsl-tab]').forEach(function (b) {
        var on = b.getAttribute('data-bsl-tab') === w;
        b.setAttribute('aria-selected', on ? 'true' : 'false');
        b.tabIndex = on ? 0 : -1;
        if (on && focus) b.focus();
      });
      $$('[data-bsl-tabpanel]').forEach(function (p) { p.hidden = p.getAttribute('data-bsl-tabpanel') !== w; });
      activateLeg();
    }
    function activateLeg() {
      if (st.step !== 3) return;
      var w = st.tab === 'drop' && !sameAddr() ? 'drop' : (st.tab === 'drop' ? null : 'pickup');
      if (w && isSelf(w)) w = null;
      if (w && pd[w]) pd[w].activate();
    }
    function paintPD() {
      var c = collection();
      setText('[data-bsl-leg-time="pickup"]', c ? fmtDate(c.dropIso) + ', ' + fmtTime(c.dropMin) : '');
      setText('[data-bsl-leg-time="drop"]', c ? fmtDate(c.iso) + ', ' + fmtTime(c.min) : '');
      var same = $('[data-bsl-drop-same]');
      if (same) same.checked = st.dropSame;
      var sw = $('[data-bsl-same-wrap]');
      if (sw) sw.hidden = isSelf('pickup') || isSelf('drop');
      var dl = $('[data-bsl-loc="drop"]');
      if (dl) dl.hidden = sameAddr() || isSelf('drop');
      var pl = $('[data-bsl-loc="pickup"]');
      if (pl) pl.hidden = isSelf('pickup');
      var nw = $('[data-bsl-note-wrap]');
      if (nw) nw.hidden = isSelf('pickup');
      var am = $('[data-bsl-agent-msg]');
      if (am) am.hidden = agentLegs() === 0;
      var ss = $('[data-bsl-same-summary]');
      if (ss) ss.textContent = sameAddr() ? (legShort('pickup') || 'Set your pickup spot first') : '';
      $$('[data-bsl-legmode]').forEach(function (r) { r.checked = r.value === (isSelf(r.getAttribute('data-bsl-legmode')) ? 'self' : 'agent'); });
      LEGS.forEach(function (w) {
        var self = isSelf(w);
        var hc = $('[data-bsl-hubcard="' + w + '"]');
        if (hc) hc.hidden = !self;
        var who = $('[data-bsl-leg-who="' + w + '"]');
        if (who) who.textContent = w === 'pickup' ? (self ? 'Drop off at the hub' : 'Agent arrives') : (self ? 'Ready to collect from' : 'Agent returns your bags');
        var sv = $('[data-bsl-legmode-save="' + w + '"]');
        if (sv) { var amt = topClass() ? selfSaving(w) : 0; sv.textContent = amt > 0 ? 'Save ' + money(amt) : ''; }
        var tab = $('[data-bsl-tab="' + w + '"]');
        if (tab) { tab.toggleAttribute('data-ok', legState(w)); tab.toggleAttribute('data-warn', legOutside(w)); }
        var sub = $('[data-bsl-tab-sub="' + w + '"]');
        if (sub) sub.textContent = self ? (w === 'pickup' ? 'You drop off at the hub' : 'You collect at the hub') :
          w === 'drop' && sameAddr() ? 'Same as pickup' : ((legOutside(w) ? 'Outside ' + SVC.km + ' km · ' : '') + (legShort(w) || 'Add address'));
      });
      paintOutside();
      var top = topClass(), hl = handlingLine();
      var fee = $('[data-bsl-handle-sum]');
      if (fee) {
        fee.hidden = !top || !hl;
        if (top && hl) {
          var ht = handlingTitle(hl), parts = [];
          if (hl.legs < 2) parts.push(handlingWho(hl).toLowerCase() + ', saving ' + money(hl.was - hl.base), handlingSplit(hl));
          parts.push(hl.legs ? handlingNote(hl).replace(/^Once per booking/, 'once per booking') : 'once per booking');
          fee.innerHTML = '<strong>' + money(hl.base) + '</strong> ' + escapeHtml(ht.charAt(0).toLowerCase() + ht.slice(1)) +
            ' <small>(' + parts.filter(Boolean).join(' · ') + ', before GST)</small>';
        }
      }
      var ls = $('[data-bsl-legsave]');
      if (ls) {
        ls.hidden = !top || !hl || hl.legs === 2;
        if (!ls.hidden) ls.innerHTML = hl.legs === 0 ?
          '<strong>No pickup or return charge.</strong> You drop off and collect at the hub and pay only the ' + money(hl.base) + ' handling charge, saving ' + money(hl.was - hl.base) + ' + GST.' :
          '<strong>You save ' + money(hl.was - hl.base) + ' + GST.</strong> Our agent handles only the ' + (isSelf('pickup') ? 'return' : 'pickup') + ' trip' + (hubBase(hl.key) ? '; the ' + money(hubBase(hl.key)) + ' handling charge still applies.' : '.');
      }
    }

    /* Step 4 review: everything chosen so far, each part one tap from its step. */
    function paintReview() {
      var rv = $('[data-bsl-review]');
      if (!rv) return;
      var c = collection();
      if (!c || !anyBags()) { rv.innerHTML = ''; return; }
      var bags = [];
      SIZE_KEYS.forEach(function (k) { if (st.counts[k] && cfg.sizes[k]) bags.push(st.counts[k] + ' × ' + escapeHtml(cfg.sizes[k].name) + (cfg.sizes[k].weight ? ' <small>(' + escapeHtml(cfg.sizes[k].weight) + ')</small>' : '')); });
      function row(label, value, step, edit) {
        return '<div class="bsl__review-row"><dt>' + label + '</dt><dd>' + value + '</dd>' +
          '<dd class="bsl__review-edit"><button type="button" data-bsl-goto="' + step + '" aria-label="' + edit + '">Edit</button></dd></div>';
      }
      var hub = escapeHtml(cfg.hubName || 'BagSafe Hub');
      rv.innerHTML = '<dl>' +
        row('Bags', bags.join(', '), 1, 'Edit bags') +
        row(isSelf('pickup') ? 'Drop-off' : 'Pickup', fmtDate(c.dropIso) + ', ' + fmtTime(c.dropMin), 2, 'Edit pickup time') +
        row(isSelf('drop') ? 'Collection' : 'Return', fmtDate(c.iso) + ', ' + fmtTime(c.min) + ' <small>(' + fmtHours(st.hours) + ')</small>', 2, 'Edit how long') +
        row(isSelf('pickup') ? 'You drop off at' : 'Pickup at', (isSelf('pickup') ? hub : escapeHtml(legShort('pickup') || '—')) + outsideTag('pickup'), 3, 'Edit pickup') +
        row(isSelf('drop') ? 'You collect at' : 'Return to', (isSelf('drop') ? hub : sameAddr() ? 'Same as pickup' : escapeHtml(legShort('drop') || '—')) + outsideTag('drop'), 3, 'Edit return') +
        '</dl>';
    }
    function outsideTag(w) {
      return legOutside(w) ? ' <span class="bsl__warn-tag">Outside ' + SVC.km + ' km area</span>' : '';
    }
    function paintOutside() {
      var note = $('[data-bsl-outside-note]');
      if (!note) return;
      var out = outsideLegs();
      note.hidden = !out.length;
      if (!out.length) return;
      var which = out.length === 2 ? 'Your pickup and return spots are' : 'Your ' + LEG_NAME[out[0].w] + ' spot is';
      note.querySelector('[data-bsl-outside-text]').textContent = which + ' outside our usual ' + SVC.km + ' km area around ' + SVC.name +
        ' (' + out.map(function (o) { return kmText(o.km); }).join(' and ') + ' away). You can still book. We’ll WhatsApp you to confirm before your agent sets out.';
    }

    /* ---------- quote ---------- */
    function planLabel(units, n) {
      var parts = [];
      units.forEach(function (u) {
        if (u.plan === 'day') parts.push(u.qty + (u.qty === 1 ? ' day' : ' days'));
        else if (u.plan === 'half') parts.push('half day');
        else parts.push(u.plan === 'h1' ? '1 hour' : '2 hours');
      });
      return parts.join(' + ');
    }
    var CLASS_NAMES = { p: 'personal item', c: 'cabin bag', k: 'check-in bag' };
    /* "Once per booking · check-in bag rate, covers all 3 bags" */
    function handlingNote(l) {
      var bags = (l.covers || []).reduce(function (n, c) { return n + c.count; }, 0);
      return 'Once per booking · ' + escapeHtml(CLASS_NAMES[l.key]) + ' rate' +
        (bags === 2 ? ', covers both bags' : bags > 2 ? ', covers all ' + bags + ' bags' : '');
    }
    /* "Pickup, return & handling", or the one leg an agent still travels. */
    function handlingTitle(l) {
      if (l.legs === 1) return (isSelf('pickup') ? 'Return' : 'Pickup') + ' & handling · one way';
      if (l.legs === 0) return 'Handling · at the hub';
      return 'Pickup, return & handling';
    }
    /* "₹49 handling + ₹65 return trip": what a reduced line is made of. */
    function handlingSplit(l) {
      var h = hubBase(l.key);
      if (!h || l.base < h) return '';
      if (l.legs === 0) return money(h) + ' handling, no pickup or return charge';
      return money(h) + ' handling + ' + money(l.base - h) + (isSelf('pickup') ? ' return trip' : ' pickup trip');
    }
    /* Who does what, for a reduced or waived handling line. */
    function handlingWho(l) {
      if (l.legs === 0) return 'You drop off and collect at the hub';
      return isSelf('pickup') ? 'You drop off at the hub' : 'You collect at the hub';
    }
    /* The heaviest bag class in the booking, which sets the handling rate. */
    function topClass() {
      var top = null;
      SIZE_KEYS.forEach(function (k) { if (st.counts[k] && cfg.sizes[k] && cfg.handling[k]) top = k; });
      return top;
    }
    function paintQuote() {
      var q = quote(cfg, st);
      var linesEl = $('[data-bsl-lines]');
      var any = q.lines.length > 0;
      if (linesEl) {
        linesEl.innerHTML = '';
        q.lines.forEach(function (l) {
          var row = document.createElement('div');
          row.className = 'bsl-quote__line';
          var dt = document.createElement('dt');
          var dd = document.createElement('dd');
          if (l.kind === 'storage') {
            var wt = cfg.sizes[l.key] && cfg.sizes[l.key].weight;
            dt.innerHTML = '<span>' + l.count + ' × ' + escapeHtml(l.name) + '</span><small>' + (wt ? escapeHtml(wt) + ' · ' : '') + planLabel(l.units) + ' each</small>';
          } else if (l.kind === 'handling') {
            dt.innerHTML = '<span>' + escapeHtml(handlingTitle(l)) + '</span><small>' + (l.legs < 2 ? [handlingWho(l), handlingSplit(l)].filter(Boolean).join(' · ') : handlingNote(l)) + '</small>';
            if (l.legs < 2) row.className += ' bsl-quote__line--saved';
          } else if (l.included) {
            row.className += ' bsl-quote__line--free';
            dt.innerHTML = '<span>' + escapeHtml(cfg.protection.label || 'Luggage cover') + '</span><small>Up to ' + escapeHtml(cfg.protection.cover || '₹1,000') + ' · free with every booking</small>';
          } else {
            dt.innerHTML = '<span>' + escapeHtml(cfg.protection.label || 'Luggage Protection Cover') + '</span><small>Per booking</small>';
          }
          if (l.kind === 'handling' && l.legs < 2) dd.innerHTML = '<s class="bsl-quote__was"><span class="bsl__sr">was </span>' + money(l.was) + '</s> ' + money(l.base);
          else dd.textContent = l.included ? 'Included' : money(l.base);
          row.appendChild(dt);
          row.appendChild(dd);
          linesEl.appendChild(row);
        });
      }
      var empty = $('[data-bsl-empty]');
      if (empty) empty.hidden = any;
      $$('[data-bsl-totals]').forEach(function (el) { el.hidden = !any; });
      setText('[data-bsl-total]', any ? money(q.total) : '—');
      setText('[data-bsl-subtotal]', money(q.subtotal));
      setText('[data-bsl-gst]', money(q.gst));
      setText('[data-bsl-grand]', money(q.total));
      setText('[data-bsl-bar-amt]', any ? money(q.total) : '—');

      var c = collection();
      var sum = $('[data-bsl-summary]');
      if (sum) {
        sum.hidden = !c;
        if (c) {
          setText('[data-bsl-sum-drop-date]', fmtDate(c.dropIso));
          setText('[data-bsl-sum-drop-time]', fmtTime(c.dropMin));
          setText('[data-bsl-sum-collect-date]', fmtDate(c.iso));
          setText('[data-bsl-sum-collect-time]', fmtTime(c.min));
          setText('[data-bsl-sum-dur]', fmtHours(st.hours));
        }
      }
      var nl = $('[data-bsl-next-label]');
      if (nl) nl.textContent = st.step === PANELS ? (any ? (cfg.labels.pay || 'Pay') + ' ' + money(q.total) : (cfg.labels.pay || 'Pay')) : (cfg.labels.next || 'Continue');
      return q;
    }
    function setText(sel, txt) { $$(sel).forEach(function (el) { el.textContent = txt; }); }
    function escapeHtml(s) { return String(s).replace(/[&<>"']/g, function (ch) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]; }); }

    /* ---------- steps ---------- */
    function paintSteps(focus) {
      $$('[data-bsl-panel]').forEach(function (p) {
        p.hidden = parseInt(p.getAttribute('data-bsl-panel'), 10) !== st.step;
      });
      $$('[data-bsl-step]').forEach(function (b) {
        var n = parseInt(b.getAttribute('data-bsl-step'), 10);
        if (n === st.step) b.setAttribute('aria-current', 'step'); else b.removeAttribute('aria-current');
        b.setAttribute('data-done', n < st.step ? 'true' : 'false');
        b.disabled = n > st.step;
      });
      $$('[data-bsl-rule]').forEach(function (r) {
        r.setAttribute('data-filled', parseInt(r.getAttribute('data-bsl-rule'), 10) < st.step ? 'true' : 'false');
      });
      var back = $('[data-bsl-back]');
      if (back) back.hidden = st.step === 1;
      root.setAttribute('data-bsl-at', String(st.step));
      if (focus) {
        var h = $('[data-bsl-panel="' + st.step + '"] [data-bsl-heading]');
        if (h) {
          h.focus({ preventScroll: true });
          var top = root.getBoundingClientRect().top;
          if (top < 0) root.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
        }
      }
    }

    function render(focusStep) {
      paintDays();
      paintTimes();
      syncUntil();
      paintBags();
      paintHandle();
      paintDuration();
      paintTip();
      paintPD();
      paintSteps(focusStep);
      paintQuote();
      paintReview();
      paintOutside();
      activateLeg();
      save();
    }

    /* ---------- validation ---------- */
    function errEl(key) { return $('[data-bsl-err="' + key + '"]'); }
    function showErr(key, msg, field) {
      var e = errEl(key);
      if (e) { e.textContent = msg; e.hidden = false; }
      if (field) {
        field.setAttribute('aria-invalid', 'true');
        if (e && e.id) field.setAttribute('aria-describedby', ((field.getAttribute('data-bsl-hint-id') || '') + ' ' + e.id).trim());
      }
    }
    function clearErr(key) {
      var e = errEl(key);
      if (e) { e.textContent = ''; e.hidden = true; }
      $$('[data-bsl-field="' + key + '"]').forEach(function (f) {
        f.removeAttribute('aria-invalid');
        var hint = f.getAttribute('data-bsl-hint-id');
        if (hint) f.setAttribute('aria-describedby', hint); else f.removeAttribute('aria-describedby');
      });
    }
    function validate(step) {
      var first = null;
      function fail(key, msg, field, focusEl) {
        showErr(key, msg, field);
        if (!first) first = focusEl || field || errEl(key);
      }
      if (step === 1) {
        clearErr('bags');
        if (!anyBags()) fail('bags', 'Add at least one bag.', null, $('[data-bsl-size] [data-bsl-inc]'));
      }
      if (step === 2) {
        clearErr('date'); clearErr('time'); clearErr('dur');
        if (!dropIso()) fail('date', 'Pick a pickup date.', dateBtn, dateBtn);
        else if (st.time === '') fail('time', 'Choose a pickup time.', timeBtn, timeBtn);
        if (st.durMode === 'until' && st.untilBad) fail('dur', st.untilBad === 'max' ? 'We can keep bags for up to ' + fmtHours(MAX_H) + '. Pick an earlier return time.' : 'Pick a return time after your pickup.', untilInput, untilInput);
        else if (!(st.hours >= 1 && st.hours <= MAX_H)) fail('dur', 'Choose between 1 hour and ' + fmtHours(MAX_H) + '.', hoursInput, hoursInput);
        else if (!collectionOk(collection())) fail('dur', 'Your return time falls outside agent hours. Pick one of the options below.', null, $('[data-bsl-fix] button'));
      }
      if (step === 3) {
        LEGS.forEach(function (w) {
          clearErr('loc-' + w); clearErr('line-' + w);
          if (isSelf(w)) { clearErr('pin-' + w); return; }
          if (w === 'drop' && sameAddr()) return;
          var p = pd[w], before = first;
          var lineEl = legEl(w, '[data-bsl-loc-line]');
          if (p) {
            var sv = p.state();
            if (sv === 'none') fail('loc-' + w, p.mapsOn() === false ? 'Type your area or street.' : 'Search your spot, use your location or tap the map.', p.input, p.input);
          }
          if (lineEl && (st[w].line || '').trim().length < 3) fail('line-' + w, 'Add your house, flat or hotel and room.', lineEl, lineEl);
          clearErr('pin-' + w);
          var pinEl = addrInput(w, 'pin'), pinV = (st[w].pin || '').trim();
          if (pinEl && pinV && !/^[1-9][0-9]{5}$/.test(pinV)) fail('pin-' + w, 'Enter a 6-digit PIN code.', pinEl, pinEl);
          if (!before && first) setTab(w);   // show the tab with the first problem before focusing it
        });
      }
      if (step === 4) {
        clearErr('name'); clearErr('phone'); clearErr('decl'); clearErr('terms');
        var nameEl = $('[data-bsl-name]'), phoneEl = $('[data-bsl-phone]');
        st.name = (nameEl.value || '').trim();
        st.phone = (phoneEl.value || '').trim();
        if (st.name.length < 2) fail('name', 'Enter your full name.', nameEl, nameEl);
        var digits = st.phone.replace(/[\s()-]/g, '').replace(/^\+?91(?=\d{10}$)/, '').replace(/^0(?=\d{10}$)/, '');
        if (!/^[6-9]\d{9}$/.test(digits)) fail('phone', 'Enter a 10-digit Indian mobile number.', phoneEl, phoneEl);
        var decl = $('[data-bsl-decl]'), terms = $('[data-bsl-terms]');
        if (decl && !decl.checked) fail('decl', 'Please confirm your bags contain no prohibited items.', decl, decl);
        if (terms && !terms.checked) fail('terms', 'Please accept the terms to continue.', terms, terms);
      }
      if (first && first.focus) first.focus();
      return !first;
    }

    /* ---------- state persistence ----------
       Saved on this device (localStorage) so a reload, the round trip to checkout or
       reopening the page later refills the booking. Kept 7 days; the step is resumed
       only within a day, after that the flow opens on step 1 with details filled in.
       The declaration and terms boxes are never saved. */
    var KEY = cfg.storageKey;
    var SAVE_TTL = 7 * 864e5, RESUME_STEP_MS = 864e5;
    var STORE = (function () {
      try { localStorage.setItem('__bs_t', '1'); localStorage.removeItem('__bs_t'); return localStorage; } catch (e) { /* blocked */ }
      try { return sessionStorage; } catch (e) { return null; }
    })();
    var restoredData = false, leaving = false;
    function forget() {
      try { if (STORE) STORE.removeItem(KEY); sessionStorage.removeItem(KEY); } catch (e) { /* nothing saved */ }
    }
    function save() {
      if (!STORE || leaving) return;
      try {
        STORE.setItem(KEY, JSON.stringify({
          v: 2, at: Date.now(), step: st.step, counts: st.counts, day: st.day, date: st.date, time: st.time,
          dur: st.dur, hours: st.hours, durMode: st.durMode, until: st.until, tt: timeTouched,
          pickup: legSave('pickup'), drop: legSave('drop'), dropSame: st.dropSame, legs: st.legs, note: st.note, tab: st.tab,
          name: st.name, phone: st.phone, protect: st.protect, wa: !!st.wa
        }));
      } catch (e) { /* storage blocked: the flow still works */ }
    }
    function legSave(w) {
      var p = pd[w] ? pd[w].get() : {};
      return { line: st[w].line, landmark: st[w].landmark, area: st[w].area, pin: st[w].pin, lat: p.lat, lng: p.lng, address: p.address, src: p.src, text: p.text };
    }
    function restore() {
      var s;
      try { s = JSON.parse((STORE && STORE.getItem(KEY)) || sessionStorage.getItem(KEY) || 'null'); } catch (e) { s = null; }
      var age = s && s.at ? Date.now() - s.at : 0;
      if (s && age > SAVE_TTL) { forget(); s = null; }
      if (!s || s.v !== 2) {
        // Fresh visit late in the day: if the default stay would end after closing,
        // start on tomorrow instead of opening on a warning.
        paintDays(); paintTimes();
        if (st.day === 'today' && !collectionOk(collection())) { st.day = 'tomorrow'; st.time = ''; }
        return;
      }
      ['day', 'date', 'time', 'dur', 'until', 'note', 'name', 'phone'].forEach(function (k) {
        if (typeof s[k] === 'string') st[k] = s[k];
      });
      if (s.counts) SIZE_KEYS.forEach(function (k) { st.counts[k] = Math.max(0, Math.min(cfg.maxPerSize || 5, parseInt(s.counts[k], 10) || 0)); });
      if (s.hours >= 1 && s.hours <= MAX_H) st.hours = s.hours;
      if (s.durMode === 'until' || s.durMode === 'len') st.durMode = s.durMode;
      st.protect = !!s.protect;
      var d0 = s.drop || {};
      st.dropSame = s.dropSame !== false || !(d0.lat != null || (d0.text || '').trim() || (d0.line || '').trim());
      if (s.tab === 'drop') st.tab = 'drop';
      if (s.legs) LEGS.forEach(function (w) { st.legs[w] = s.legs[w] === 'self' ? 'self' : 'agent'; });
      LEGS.forEach(function (w) {
        var L = s[w] || {};
        st[w] = {};
        Object.keys(ADDR_KEYS).forEach(function (k) {
          st[w][k] = typeof L[k] === 'string' ? L[k] : '';
          var el = addrInput(w, k);
          if (el) el.value = st[w][k];
        });
        if (pd[w] && (L.lat != null || L.text)) pd[w].set(L);
      });
      timeTouched = !!s.tt;
      if (st.day === 'other' && (!st.date || st.date < nowIST().iso)) st.day = 'today';
      st.wa = !!s.wa;
      var waEl = $('[data-bsl-wa]');
      if (waEl) waEl.checked = st.wa;
      var f = { '[data-bsl-name]': st.name, '[data-bsl-phone]': st.phone, '[data-bsl-agent-note]': st.note };
      Object.keys(f).forEach(function (sel) { var el = $(sel); if (el && f[sel]) el.value = f[sel]; });
      var po = $('[data-bsl-protect]');
      if (po) po.checked = st.protect;
      // Only reopen a later step if everything before it is still valid.
      paintDays(); paintTimes(); syncUntil();
      var target = age < RESUME_STEP_MS ? Math.min(PANELS, parseInt(s.step, 10) || 1) : 1;
      restoredData = anyBags() || !!(st.name || st.phone || st.note) || LEGS.some(function (w) { return !!(st[w].line || (s[w] && (s[w].text || s[w].lat != null))); });
      st.step = 1;
      while (st.step < target && quietValid(st.step)) st.step++;
      setTab(st.tab);
    }
    /* Is step n complete? Used only to decide how far a restored session may reopen. */
    function quietValid(n) {
      if (n === 1) return anyBags();
      if (n === 2) return !!dropIso() && st.time !== '' && st.hours >= 1 && st.hours <= MAX_H && !st.untilBad && collectionOk(collection());
      if (n === 3) return legState('pickup') && legState('drop') && LEGS.every(function (w) { var v = (st[w].pin || '').trim(); return isSelf(w) || !v || /^[1-9][0-9]{5}$/.test(v); });
      return false;
    }

    /* ---------- checkout ---------- */
    function makeRef() {
      var a = Date.now().toString(36).toUpperCase().slice(-4);
      var b = Math.floor(Math.random() * 1296).toString(36).toUpperCase();
      return 'BSL-' + a + (b.length < 2 ? '0' + b : b);
    }
    function post(url, body) {
      return fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body || {})
      }).then(function (res) {
        if (res.ok) return res.json().catch(function () { return {}; });
        return res.json().catch(function () { return {}; }).then(function (j) {
          throw new Error(j.description || j.message || ('Request failed (' + res.status + ')'));
        });
      });
    }
    function status(msg) {
      var el = $('[data-bsl-status]');
      if (!el) return;
      el.textContent = msg || '';
      el.hidden = !msg;
    }

    /* One readable line per leg for the order: address, landmark, place, distance, map link. */
    function legOrder(w) {
      var L = st[w], p = pd[w] ? pd[w].get() : {};
      var parts = [L.line.trim(), (L.area || '').trim(), (L.pin || '').trim() ? 'PIN ' + L.pin.trim() : ''];
      if (L.landmark.trim()) parts.push('Landmark: ' + L.landmark.trim());
      if (p.address) parts.push(p.address);
      else if (p.text && p.lat == null) parts.push(p.text);
      if (p.lat != null) {
        parts.push(kmText(haversineKm(SVC, p)) + ' from ' + SVC.name);
        parts.push('https://maps.google.com/?q=' + p.lat.toFixed(6) + ',' + p.lng.toFixed(6));
      }
      return parts.filter(Boolean).join(' · ');
    }
    var busy = false;
    function checkout() {
      if (busy) return;
      var q = quote(cfg, st);
      if (!q.lines.length) return;
      busy = true;
      var btn = $('[data-bsl-next]');
      btn.disabled = true;
      var label = $('[data-bsl-next-label]');
      if (label) label.textContent = cfg.labels.working || 'Taking you to payment…';
      status('');
      save();

      var c = collection();
      var ref = makeRef();
      var bags = [];
      SIZE_KEYS.forEach(function (k) { if (st.counts[k]) bags.push(st.counts[k] + ' × ' + cfg.sizes[k].name + (cfg.sizes[k].weight ? ' (' + cfg.sizes[k].weight + ')' : '')); });
      var dropTxt = fmtDateLong(c.dropIso) + ', ' + fmtTime(c.dropMin);
      var collectTxt = fmtDateLong(c.iso) + ', ' + fmtTime(c.min);

      var mainProps = {
        'Booking ref': ref,
        'Service': 'Luggage storage',
        'Hub': cfg.hubName,
        // Keys stay "Agent pickup" / "Agent drop": the backend reads the times from them.
        // A leg the customer handles names the hub instead of an address.
        'Agent pickup': dropTxt + ' · ' + (isSelf('pickup') ? 'Customer drops off at ' + cfg.hubName : legOrder('pickup')),
        'Agent drop': collectTxt + ' · ' + (isSelf('drop') ? 'Customer collects from ' + cfg.hubName : sameAddr() ? 'Same as pickup' : legOrder('drop')),
        'Hub legs': (isSelf('pickup') ? 'Customer drop-off at hub' : 'Agent pickup') + ' · ' + (isSelf('drop') ? 'Customer collection at hub' : 'Agent return'),
        'Duration': fmtHours(st.hours),
        'Bags': bags.join(', '),
        'Name': st.name,
        'Mobile': (function () {
          var d = st.phone.replace(/[\s()-]/g, '').replace(/^\+?91(?=\d{10}$)/, '').replace(/^0(?=\d{10}$)/, '');
          return /^[6-9]\d{9}$/.test(d) ? '+91' + d : st.phone;
        })(),
        'WhatsApp updates': ($('[data-bsl-wa]') || {}).checked ? 'Yes' : 'No',
        'Declaration': 'No prohibited items (confirmed)'
      };
      if (st.note.trim() && !isSelf('pickup')) mainProps['Note for agent'] = st.note.trim();
      var checks = [];
      if (LEGS.some(function (w) { return !isSelf(w) && !(w === 'drop' && sameAddr()) && pd[w] && pd[w].get().lat == null; })) {
        checks.push('Not verified (map unavailable). Confirm the spot on WhatsApp');
      }
      outsideLegs().forEach(function (o) {
        checks.push((o.w === 'pickup' ? 'Pickup' : 'Return') + ' is outside the ' + SVC.km + ' km area (' + kmText(o.km) + ' from ' + SVC.name + '). Confirm with the customer on WhatsApp');
      });
      if (checks.length) mainProps['Location check'] = checks.join(' | ');
      if (cfg.protection && cfg.protection.mode === 'included') {
        mainProps['Luggage cover'] = 'Included free · up to ' + (cfg.protection.cover || '₹1,000');
      }
      var storageIds = {};
      SIZE_KEYS.forEach(function (k) {
        if (!cfg.sizes[k]) return;
        PLAN_KEYS.forEach(function (pk) { if (cfg.sizes[k].v[pk]) storageIds[cfg.sizes[k].v[pk].id] = true; });
      });

      var items = [];
      var mainDone = false;
      Object.keys(q.cart).forEach(function (id) {
        var props = { 'Booking ref': ref };
        if (storageIds[id] && !mainDone) { props = mainProps; mainDone = true; }
        items.push({ id: parseInt(id, 10), quantity: q.cart[id], properties: props });
      });
      // Blank every shipping attribute so stale flight/pickup data never rides on a
      // storage order (cart/clear.js keeps attributes).
      var attrs = {};
      SHIP_ATTRS.forEach(function (k) { attrs[k] = ''; });

      post('/cart/clear.js')
        .then(function () { return post('/cart/add.js', { items: items }); })
        .then(function () { return post('/cart/update.js', { attributes: attrs }); })
        .then(function () {
          root.dispatchEvent(new CustomEvent('bsl:checkout', { bubbles: true, detail: { ref: ref, total: q.total } }));
          if (root.hasAttribute('data-bsl-no-redirect')) { busy = false; return; }
          window.location.href = '/checkout';
        })
        .catch(function (err) {
          busy = false;
          btn.disabled = false;
          paintQuote();
          status('We couldn’t start checkout: ' + (err && err.message ? err.message : 'please try again') + '. Nothing has been charged.');
        });
    }

    /* ---------- events ---------- */
    root.addEventListener('click', function (e) {
      var t = e.target.closest('button');
      if (!t || !root.contains(t)) return;
      if (t.hasAttribute('data-bsl-inc') || t.hasAttribute('data-bsl-dec')) {
        var key = t.closest('[data-bsl-size]').getAttribute('data-bsl-size');
        var n = st.counts[key] + (t.hasAttribute('data-bsl-inc') ? 1 : -1);
        st.counts[key] = Math.max(0, Math.min(cfg.maxPerSize || 5, n));
        clearErr('bags');
        render();
      } else if (t.hasAttribute('data-bsl-next')) {
        if (!validate(st.step)) return;
        if (st.step < PANELS) { st.step++; render(true); } else checkout();
      } else if (t.hasAttribute('data-bsl-back')) {
        if (st.step > 1) { st.step--; render(true); }
      } else if (t.hasAttribute('data-bsl-step')) {
        var n2 = parseInt(t.getAttribute('data-bsl-step'), 10);
        if (n2 < st.step) { st.step = n2; render(true); }
      } else if (t.hasAttribute('data-bsl-hours-step')) {
        st.hours = Math.max(1, Math.min(MAX_H, st.hours + parseInt(t.getAttribute('data-bsl-hours-step'), 10)));
        st.dur = 'custom';
        clearErr('dur');
        render();
      } else if (t.hasAttribute('data-bsl-extend')) {
        setHours(parseInt(t.getAttribute('data-bsl-extend'), 10));
        var j = $('[data-bsl-collect]');
        if (j) { j.setAttribute('data-flash', ''); setTimeout(function () { j.removeAttribute('data-flash'); }, 700); }
      } else if (t.hasAttribute('data-bsl-tab')) {
        setTab(t.getAttribute('data-bsl-tab'));
      } else if (t.hasAttribute('data-bsl-goto')) {
        var g = parseInt(t.getAttribute('data-bsl-goto'), 10);
        if (g < st.step) { st.step = g; render(true); }
      } else if (t.hasAttribute('data-bsl-resume-reset')) {
        leaving = true;   // no save on the way out
        forget();
        window.location.replace(window.location.href.split('#')[0]);
      } else if (t.hasAttribute('data-bsl-bar-total')) {
        var qa = $('[data-bsl-quote]');
        if (qa) {
          qa.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
          qa.focus({ preventScroll: true });
        }
      }
    });

    root.addEventListener('change', function (e) {
      var t = e.target;
      if (t.hasAttribute('data-bsl-day')) {
        st.day = t.value;
        if (!timeTouched) st.time = '';   // re-suggest a sensible time for the new day
        if (st.day === 'other' && (!st.date || st.date < nowIST().iso)) st.date = addDays(nowIST().iso, 2);
        clearErr('date'); clearErr('time');
        render();
        if (st.day === 'other' && dateBtn) dateBtn.focus();
      } else if (t.hasAttribute('data-bsl-durmode')) {
        st.durMode = t.value;
        if (st.durMode === 'until') {
          var iso = dropIso();
          st.until = iso && st.time !== '' ? dtLocal(absOf(iso, parseInt(st.time, 10)) + st.hours * 60) : '';
        }
        clearErr('dur');
        render();
        if (st.durMode === 'until' && untilTimeBtn) untilTimeBtn.focus();
      } else if (t.hasAttribute('data-bsl-dur')) {
        st.dur = t.value;
        if (st.dur !== 'custom') st.hours = parseInt(st.dur, 10);
        clearErr('dur');
        render();
        if (st.dur === 'custom' && hoursInput) hoursInput.focus();
      } else if (t.hasAttribute('data-bsl-drop-same')) {
        st.dropSame = t.checked;
        clearErr('loc-drop'); clearErr('line-drop');
        render();
        if (!st.dropSame && pd.drop) { pd.drop.activate(); pd.drop.input.focus(); }
      } else if (t.hasAttribute('data-bsl-legmode')) {
        var lw = t.getAttribute('data-bsl-legmode');
        st.legs[lw] = t.value === 'self' ? 'self' : 'agent';
        ['loc-', 'line-', 'pin-'].forEach(function (k) { clearErr(k + lw); });
        setTab(lw);
        render();
      } else if (t.hasAttribute('data-bsl-protect')) {
        st.protect = t.checked; render();
      } else if (t.hasAttribute('data-bsl-wa')) {
        st.wa = t.checked; save();
      } else if (t.hasAttribute('data-bsl-decl')) {
        if (t.checked) clearErr('decl');
      } else if (t.hasAttribute('data-bsl-terms')) {
        if (t.checked) clearErr('terms');
      }
    });

    root.addEventListener('input', function (e) {
      var t = e.target;
      if (t.hasAttribute('data-bsl-hours')) {
        var h = parseInt(t.value, 10);
        if (h >= 1 && h <= MAX_H) { st.hours = h; st.dur = 'custom'; clearErr('dur'); paintDuration(); paintTip(); paintQuote(); paintReview(); save(); }
      } else if (t.hasAttribute('data-bsl-name')) { st.name = t.value; clearErr('name'); save(); }
      else if (t.hasAttribute('data-bsl-phone')) { st.phone = t.value; clearErr('phone'); save(); }
      else if (t.hasAttribute('data-bsl-addr-field')) {
        var w = t.closest('[data-bsl-loc]').getAttribute('data-bsl-loc');
        var ak = t.getAttribute('data-bsl-addr-field');
        st[w][ak] = t.value;
        t.removeAttribute('data-auto');   // the customer owns this value now
        syncChip(w);
        if (ak === 'line' || ak === 'pin') clearErr(ak + '-' + w);
        paintPD(); save();
      }
      else if (t.hasAttribute('data-bsl-agent-note')) { st.note = t.value; save(); }
    });

    var tablist = $('[data-bsl-pd] [role="tablist"]');
    if (tablist) tablist.addEventListener('keydown', function (e) {
      var i = LEGS.indexOf(st.tab), n = LEGS.length;
      var k = e.key === 'ArrowRight' ? i + 1 : e.key === 'ArrowLeft' ? i - 1 : e.key === 'Home' ? 0 : e.key === 'End' ? n - 1 : null;
      if (k === null) return;
      e.preventDefault();
      setTab(LEGS[(k + n) % n], true);
    });

    // Enter in a text field moves forward instead of submitting the form.
    var form = $('[data-bsl-form]');
    if (form) form.addEventListener('submit', function (e) {
      e.preventDefault();
      var next = $('[data-bsl-next]');
      if (next && !next.disabled) next.click();
    });

    // Back from checkout via bfcache: re-enable Pay and refresh the clock-based slots.
    window.addEventListener('pageshow', function (e) {
      if (e.persisted) { busy = false; var b = $('[data-bsl-next]'); if (b) b.disabled = false; render(); }
    });
    document.addEventListener('visibilitychange', function () { if (!document.hidden) render(); else save(); });
    window.addEventListener('pagehide', save);

    paintRates();
    restore();
    render();
    var resumeBar = $('[data-bsl-resume]');
    if (resumeBar) resumeBar.hidden = !restoredData;
    root.setAttribute('data-bsl-ready', '');
    root.__bslState = st;
    root.__bslQuote = function () { return quote(cfg, st); };
    root.__bslSetPin = function (w, lat, lng, address) { if (pd[w]) pd[w].setPin(lat, lng, address); };
    root.__bslOutside = function () { return outsideLegs(); };
    root.__bslPinAt = function (w, lat, lng) { if (pd[w]) pd[w].pinAt(lat, lng); };
    root.__bslFill = function (w, components, formatted, name) { fillLeg(w, addressParts(components, formatted), name || ''); };
    root.__bslLeg = function (w) { return pd[w] ? { state: pd[w].state(), km: pd[w].km(), pick: pd[w].get(), maps: pd[w].mapsOn() } : null; };
  }

  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-bsl]'), init);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', boot);
})(typeof window !== 'undefined' ? window : globalThis);
